import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Banknote,
  Check,
  CheckCircle2,
  ChevronRight,
  Copy,
  CreditCard,
  Crown,
  ExternalLink,
  FileText,
  Gift,
  Loader2,
  Minus,
  Package,
  PlusCircle,
  Plus,
  Receipt,
  Search,
  ShoppingBag,
  Smartphone,
  Sparkles,
  User,
  Wallet,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Label,
  Modal,
  ModalFooter,
  Select,
  Skeleton,
  useConfirm,
  useToast,
} from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { mountCardField, tokeniseCard } from "@/lib/stripe-card";
import { formatDate, formatMoney, formatPhone, formatTime, fullName } from "@/lib/format";
import type { Appointment, Contact, Fulfilment, GiftCard, PackageBalance, Product, Service, SpaPackage } from "@/lib/types";
import { cn } from "@/lib/utils";

/* ─── Local models ─────────────────────────────────────────────────────────── */

interface PosConfig {
  tax_rate_pct: number;
  currency: string;
  timezone: string;
  /**
   * Is there a card machine of her own to press "already paid" for?
   *
   * "It says paid on the machine but it did not pay it." That button writes a
   * sale down as paid and charges nothing, which is right with a terminal on
   * the desk and a hole in the books without one. Off until Settings says
   * otherwise, and the server refuses it either way.
   */
  own_card_machine?: boolean;
  /**
   * What a client pays on top for paying by card.
   *
   * "I need the clients to be charged the cc fees … I have already gave
   *  notice to my clients that credit card fees apply." 0 means off.
   */
  card_fee_pct?: number;
  card_fee_label?: string;
  /** The processor's PUBLIC key, so the card field can live on this page. */
  card_key?: string | null;
}

/** A visit is one or more services back-to-back — one ticket line each. */
interface ApptSegment {
  service_id: string | null;
  service_name: string | null;
  price_cents: number | null;
  provider_name: string | null;
  room_name: string | null;
  starts_at: string;
  /** Boosters added into this treatment — each rings up as its own line. */
  addons?: { service_id: string; name: string; price_cents: number }[];
}

/** `segments` isn't on the shared Appointment type yet — the API sends it. */
type VisitAppointment = Appointment & { segments?: ApptSegment[] };

/** `/pos/memberships` returns raw rows — bigint/numeric arrive as strings. */
interface MembershipRow {
  id: string;
  name: string;
  description: string | null;
  price_cents: string | number;
  interval: string;
  credit_cents: string | number;
  discount_pct: string | number;
}

interface Membership {
  id: string;
  name: string;
  description: string | null;
  price_cents: number;
  interval: "month" | "two_months" | "year";
  credit_cents: number;
  discount_pct: number;
}

function toMembership(r: MembershipRow): Membership {
  return {
    id: r.id,
    name: r.name,
    description: r.description,
    price_cents: Number(r.price_cents) || 0,
    interval: r.interval === "year" ? "year" : r.interval === "two_months" ? "two_months" : "month",
    credit_cents: Number(r.credit_cents) || 0,
    discount_pct: Number(r.discount_pct) || 0,
  };
}

/** "/month", "/2 months", "/year". */
function perCycle(interval: Membership["interval"]): string {
  return interval === "year" ? "/year" : interval === "two_months" ? "/2 months" : "/month";
}

/** "every month", "every 2 months", "every year". */
function everyCycle(interval: Membership["interval"]): string {
  return interval === "year" ? "every year" : interval === "two_months" ? "every 2 months" : "every month";
}

/** One burnt package session — the counter's answer to "I only got 11 of 12". */
interface PackageRedemption {
  id: string;
  package_name: string | null;
  service_name: string | null;
  sessions: number;
  created_at: string;
  invoice_number: string | null;
}

type LineKind = "service" | "product" | "package" | "membership" | "gift_card" | "custom";

/** Her three answers, in her words, in the order she says them. */
const FULFILMENTS: { v: Fulfilment; label: string }[] = [
  { v: "in_lounge", label: "In lounge" },
  { v: "take_home", label: "Take home" },
  { v: "mail_out",  label: "Mail out" },
];

/**
 * Why a ticket was discounted, in her words.
 *
 * "need reasons why we are discounting like included with purchase,
 *  Police/Fire, included with service - we do not charge for IV drip
 *  consultations we have them in blvd as well"
 *
 * Kept as plain text on the sale so a refund or a report later says why the
 * money came off, which a bare dollar amount never could.
 */
const DISCOUNT_REASONS = [
  "Included with purchase",
  "Included with service",
  "Police / Fire",
  "Friends and family",
  "Making it right",
  "Promotion",
] as const;

interface Line {
  uid: string;
  kind: LineKind;
  /** service_id / product_id / package_id / membership_id — null for gift_card + custom. */
  refId: string | null;
  name: string;
  caption?: string;
  unitCents: number;
  qty: number;
  taxable: boolean;
  /** Send unit_amount_cents to the server (appointment price override). */
  unitOverride: boolean;
  /** Service lines only — pay this line with a package session. */
  redeemBalanceId: string | null;
  /**
   * "one bill but two separate orders" — whose treatment this is, when it is
   * not the person paying.
   *
   * The id goes to the server with the line: "the services have to be in the
   * specific client chart … each patient gets a seperated receipt with only
   * there services on the receipt". A name alone only ever labelled the till.
   */
  forName?: string | null;
  forContactId?: string | null;
  /** Gift-card lines only. */
  recipientName?: string;
  /** The number printed on a physical card, when she is loading one. */
  giftCode?: string;
  /**
   * Package lines only — how the client is getting it.
   *
   * "at checkout it did not ask me how I was selling - In lounge, take home
   *  or post out." Asked on every package now, so this is always set.
   */
  fulfilment?: Fulfilment;
  /**
   * This line's own discount, and why.
   *
   * "each service or product needs the option to discount with options why -
   *  this is important so when they get receipt they will see percentage off
   *  or free or partial discount"
   */
  discountCents?: number;
  discountReason?: string;
  /** Package lines only — doses per package, for the hand-over caption. */
  sessions?: number;
}

interface ClientLite {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
}

type PayMethod = "cash" | "card_external" | "card_terminal" | "card_link" | "card_on_file" | "card_manual" | "none";

/**
 * A reader paired to the practice's Stripe, as the till sees it.
 *
 * "Matt who sets up the card equipment I have" - her Stripe Reader S700. The
 * till puts the amount ON it rather than asking her to key the total twice.
 */
interface PairedReader {
  id: string;
  label: string | null;
  device_type: string | null;
  status: string;
  busy: boolean;
}

interface CheckoutResult {
  invoice_id: string;
  number: string;
  total_cents: number;
  status: "paid" | "open" | "partly_paid";
  gift_card_applied_cents: number;
  credit_applied_cents: number;
  pay_url?: string;
  /** Brand and last four, when the card was taken on the reader. */
  charged_card?: string | null;
  /** The link also starts automatic membership billing. */
  auto_renews?: boolean;
  /** Rung up in practice mode: nothing charged, nothing sent. */
  practice?: boolean;
  method: PayMethod;
  receiptEmail: string | null;
  /** Part of a split: what is still owed on this sale. */
  balance_cents?: number;
}

interface AppliedGiftCard {
  id: string;
  code: string;
  balance_cents: number;
}

/**
 * Part paid, the rest still owing.
 *
 *   "if the bill is 1000 and they want to pay 300 cash 200 on one card and
 *    500 on another how do I do that"
 *
 * One ticket, one receipt at the end. This is the screen between the first
 * payment and the last: what has gone on so far, what is left, and the same
 * buttons for the rest.
 */
function BalancePanel({ result, liveReader, cards, onPaid, onNewSale }: {
  result: CheckoutResult;
  liveReader: PairedReader | null;
  /** Their cards on file - this is where "200 on one, 500 on the other" happens. */
  cards: { id: string; label: string; is_default?: boolean; funding?: string | null }[];
  onPaid: (r: CheckoutResult) => void;
  onNewSale: () => void;
}) {
  const toast = useToast();
  const [balance, setBalance] = useState(result.balance_cents ?? 0);
  const [taken, setTaken] = useState<{ type: string; amount_cents: number }[]>([]);
  const [amount, setAmount] = useState(result.balance_cents ?? 0);
  const [busy, setBusy] = useState<string | null>(null);
  const [collecting, setCollecting] = useState(false);
  const [cardId, setCardId] = useState<string | null>(
    cards.find((c) => c.is_default)?.id ?? cards[0]?.id ?? null);

  const refresh = useCallback(async () => {
    try {
      const d = await api.get<{ status: string; balance_cents: number; paid_cents: number;
        payments: { type: string; amount_cents: number }[] }>(`/pos/invoices/${result.invoice_id}/balance`);
      setTaken(d.payments ?? []);
      setBalance(d.balance_cents);
      setAmount((a) => Math.min(a || d.balance_cents, d.balance_cents) || d.balance_cents);
      if (d.status === "paid") {
        onPaid({ ...result, status: "paid", balance_cents: 0 });
        toast.success("Paid in full", `${result.number} · ${formatMoney(result.total_cents)}`);
      }
      return d;
    } catch { return null; }
  }, [result, onPaid, toast]);

  useEffect(() => { void refresh(); }, [refresh]);

  // The reader is a wait, so watch it the same way the full-sale screen does.
  useEffect(() => {
    if (!collecting) return;
    const t = setInterval(async () => {
      try {
        const d = await api.get<{ status: string }>(`/pos/terminal/collect/${result.invoice_id}`);
        if (d.status === "collecting") return;
        setCollecting(false);
        if (d.status === "failed" || d.status === "cancelled") toast.error("That card didn't go through");
        void refresh();
      } catch { /* a blip between polls is not a failure */ }
    }, 1500);
    return () => clearInterval(t);
  }, [collecting, result.invoice_id, refresh, toast]);

  const take = async (method: PayMethod) => {
    if (amount <= 0) return;
    setBusy(method);
    try {
      const d = await api.post<{ status: string; balance_cents: number }>(
        `/pos/invoices/${result.invoice_id}/pay`,
        { payment_method: method, amount_cents: Math.min(amount, balance),
          ...(method === "card_terminal" && liveReader ? { reader_id: liveReader.id } : {}),
          ...(method === "card_on_file" && cardId ? { card_id: cardId } : {}) });
      if (d.status === "collecting") { setCollecting(true); return; }
      await refresh();
    } catch (e) {
      toast.error("Couldn't take that payment", errMessage(e));
    } finally { setBusy(null); }
  };

  /** Bin a saved ticket nobody has paid on - it has handed over nothing. */
  const scrapTicket = async () => {
    if (!window.confirm("Throw this saved ticket away? Nothing has been paid on it.")) return;
    setBusy("void");
    try {
      await api.post(`/pos/orders/${result.invoice_id}/void`, { reason: "Saved ticket thrown away at the till" });
      toast.success("Ticket thrown away");
      onNewSale();
    } catch (e) {
      toast.error("Couldn't throw that ticket away", errMessage(e));
    } finally { setBusy(null); }
  };

  const paidSoFar = result.total_cents - balance;
  // A saved ticket nobody has paid a penny on is not "part paid", and calling
  // it that made the books look wrong to her.
  const held = paidSoFar <= 0;
  return (
    <div className="mx-auto max-w-lg space-y-4">
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
        <p className="text-sm font-medium text-amber-200">
          {held ? `Saved ticket - ${formatMoney(balance)} to pay` : `Part paid - ${formatMoney(balance)} still owing`}
        </p>
        <p className="mt-0.5 text-xs text-zinc-400">
          {result.number} · {formatMoney(result.total_cents)} in total.{" "}
          {held
            ? "It will be waiting here until they come to pay. Nothing is sent and no packages are handed over yet."
            : "Nothing is sent and no packages are handed over until the whole bill is in."}
        </p>
        {taken.length > 0 && (
          <ul className="mt-3 space-y-1 border-t border-amber-500/20 pt-3 text-xs text-zinc-300">
            {taken.map((t, i) => (
              <li key={i} className="flex justify-between tabular-nums">
                <span>{PAY_LABEL[t.type] ?? t.type}</span>
                <span>{formatMoney(t.amount_cents)}</span>
              </li>
            ))}
            <li className="flex justify-between border-t border-amber-500/20 pt-1 font-medium text-amber-200 tabular-nums">
              <span>Taken so far</span><span>{formatMoney(paidSoFar)}</span>
            </li>
          </ul>
        )}
      </div>

      {collecting ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 text-center">
          <p className="text-sm text-zinc-200">Waiting for the card on the reader…</p>
          <p className="mt-1 text-xs text-zinc-500">Ask them to tap, insert or swipe.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4">
          <Label htmlFor="bal-amt">Take next</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-500">$</span>
            <Input
              id="bal-amt"
              inputMode="decimal"
              value={(amount / 100).toFixed(2)}
              onChange={(e) => {
                const n = Math.round(parseFloat(e.target.value.replace(/[^0-9.]/g, "")) * 100);
                setAmount(Number.isFinite(n) ? Math.max(0, Math.min(n, balance)) : 0);
              }}
              className="w-32 tabular-nums"
            />
            <button type="button" onClick={() => setAmount(balance)}
              className="rounded text-xs font-medium text-indigo-300 hover:text-indigo-200">
              all of it
            </button>
          </div>
          <div className="mt-3 space-y-2">
            <Button className="w-full" leftIcon={<Banknote className="h-4 w-4" />}
              loading={busy === "cash"} disabled={busy !== null || amount <= 0}
              onClick={() => void take("cash")}>Cash</Button>
            {liveReader && (
              <Button className="w-full" variant="secondary"
                loading={busy === "card_terminal"} disabled={busy !== null || amount <= 0}
                onClick={() => void take("card_terminal")}>Charge on the reader</Button>
            )}
            {cards.length > 0 && (
              <>
                {cards.length > 1 && (
                  <Select
                    value={cardId ?? ""}
                    onChange={(e) => setCardId(e.target.value)}
                    className="h-8 text-xs"
                    aria-label="Which card to charge"
                  >
                    {cards.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}{c.is_default ? " (default)" : ""}</option>
                    ))}
                  </Select>
                )}
                <Button className="w-full" variant="secondary"
                  loading={busy === "card_on_file"} disabled={busy !== null || amount <= 0}
                  onClick={() => void take("card_on_file")}>
                  {cards.length > 1 ? "Charge that card" : "Charge the card on file"}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="space-y-1">
        <button type="button" onClick={onNewSale}
          className="w-full rounded-lg py-2 text-xs text-zinc-500 hover:text-zinc-300">
          {held ? "Leave it saved and start a new sale" : "Leave it owing and start a new sale"}
        </button>
        {held && (
          <button type="button" onClick={() => void scrapTicket()} disabled={busy !== null}
            className="w-full rounded-lg py-2 text-xs text-zinc-600 hover:text-rose-300 disabled:opacity-50">
            Throw this ticket away
          </button>
        )}
      </div>
    </div>
  );
}

/** What the desk calls each way money came in. */
const PAY_LABEL: Record<string, string> = {
  cash: "Cash",
  card_external: "Her own card machine",
  card_terminal: "Reader",
  card_on_file: "Card on file",
  card_link: "Payment link",
  stripe: "Card",
  manual: "Cash",
};

function uid(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function errMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

/** "12.50" / "$12.5" / "12" → cents. Invalid → 0. */
function parseDollars(s: string): number {
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

const CARD_CLASS = "rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5";

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export function CheckoutPage() {
  const [tab, setTab] = useState<"sale" | "giftcards">("sale");
  const practice = usePracticeMode();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Checkout</h1>
          <p className="mt-1 text-sm text-zinc-500">Ring up a visit: services, retail, tip, and payment.</p>
        </div>
        <PracticeSwitch practice={practice} />
      </div>
      {practice.state?.on && <PracticeBanner practice={practice} />}

      <div className="flex gap-1 border-b border-zinc-800">
        {([
          { k: "sale" as const, l: "Sale", Icon: Receipt },
          { k: "giftcards" as const, l: "Gift cards", Icon: Gift },
        ]).map(({ k, l, Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium",
              tab === k
                ? "border-indigo-500 text-zinc-100"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            )}
          >
            <Icon className="h-4 w-4" />
            {l}
          </button>
        ))}
      </div>

      {tab === "sale" ? <SaleTab practiceOn={practice.state?.on === true} onPracticeSale={practice.reload} /> : <GiftCardsTab />}
    </div>
  );
}

/* ─── Practice mode ────────────────────────────────────────────────────────── */
// "If I use the checkout now can it be in test mode for a little?" Practice
// sales charge nobody, text nobody, stay out of the reports and the cash
// drawer, and are deleted (with everything they added) when practice ends.

interface PracticeState { on: boolean; since: string | null; sales: number }

function usePracticeMode() {
  const [state, setState] = useState<PracticeState | null>(null);
  const reload = useCallback(() => {
    api.get<PracticeState>("/pos/practice").then(setState).catch(() => setState(null));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { state, reload, setState };
}

function PracticeSwitch({ practice }: { practice: ReturnType<typeof usePracticeMode> }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const s = practice.state;
  if (!s) return null;
  const toggle = async () => {
    if (s.on) {
      const ok = await confirm({
        title: "Turn off practice mode?",
        description: s.sales > 0
          ? `Your ${s.sales} practice sale${s.sales === 1 ? "" : "s"} will be deleted, along with everything they added: packages, memberships, gift cards, account credit and stock changes. Real sales aren't touched.`
          : "Checkout goes back to taking real payments.",
        confirmLabel: s.sales > 0 ? "Delete them and turn off" : "Turn off",
        danger: s.sales > 0,
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const d = await api.put<{ on: boolean; cleared: number }>("/pos/practice", { on: !s.on });
      toast.success(d.on ? "Practice mode is on" : d.cleared > 0 ? `Practice mode off. ${d.cleared} practice sale${d.cleared === 1 ? "" : "s"} deleted.` : "Practice mode is off");
      practice.reload();
    } catch (e) {
      toast.error("Couldn't change practice mode", errMessage(e));
    } finally { setBusy(false); }
  };
  return (
    <label className={cn(
      "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm",
      s.on ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-zinc-800 text-zinc-300 hover:border-zinc-700",
    )}>
      <input type="checkbox" checked={s.on} disabled={busy} onChange={() => void toggle()} className="h-4 w-4 accent-amber-500" />
      Practice mode
    </label>
  );
}

function PracticeBanner({ practice }: { practice: ReturnType<typeof usePracticeMode> }) {
  const toast = useToast();
  const confirm = useConfirm();
  const s = practice.state!;
  const clear = async () => {
    const ok = await confirm({
      title: `Delete ${s.sales} practice sale${s.sales === 1 ? "" : "s"}?`,
      description: "Everything they added goes too: packages, memberships, gift cards, account credit and stock changes. Practice mode stays on.",
      confirmLabel: "Delete practice sales",
      danger: true,
    });
    if (!ok) return;
    try {
      const d = await api.post<{ cleared: number }>("/pos/practice/clear");
      toast.success(`${d.cleared} practice sale${d.cleared === 1 ? "" : "s"} deleted`);
      practice.reload();
    } catch (e) {
      toast.error("Couldn't delete them", errMessage(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
      <p className="min-w-0">
        <span className="font-semibold">Practice mode is on.</span>{" "}
        Nothing is charged, no texts or receipts go to clients, and practice sales stay out of your reports and the cash drawer.
        {s.sales > 0 ? ` ${s.sales} practice sale${s.sales === 1 ? "" : "s"} so far.` : ""}
      </p>
      {s.sales > 0 && (
        <Button size="sm" variant="secondary" onClick={() => void clear()}>Delete practice sales</Button>
      )}
    </div>
  );
}

/* ─── Sale tab ─────────────────────────────────────────────────────────────── */

function SaleTab({ practiceOn, onPracticeSale }: { practiceOn: boolean; onPracticeSale: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get("appointment_id");
  // "Renew at checkout" from a client's Membership tab lands here with both.
  const prefillContactId = searchParams.get("contact_id");
  const prefillMembershipId = searchParams.get("membership_id");

  // Catalog + config
  const [config, setConfig] = useState<PosConfig | null>(null);
  /** The processor's public key, so the card field can live on this page. */
  const [cardKey, setCardKey] = useState<string | null>(null);
  /** Her card field is open, with the client standing there. */
  const [typeCard, setTypeCard] = useState(false);
  const [services, setServices] = useState<Service[] | null>(null);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [packages, setPackages] = useState<SpaPackage[] | null>(null);
  const [memberships, setMemberships] = useState<Membership[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Ticket
  const [client, setClient] = useState<ClientLite | null>(null);
  /* "how do I combine clients at checkout - for one bill but two separate
     orders" — the other people whose visits this one bill covers. Their
     treatments come onto the ticket tagged with their name, and paying
     closes their visit too. */
  const [alsoFor, setAlsoFor] = useState<{ contact_id: string; appointment_id: string | null; name: string }[]>([]);
  const [addingPerson, setAddingPerson] = useState(false);
  const [balances, setBalances] = useState<PackageBalance[]>([]);
  const [redemptions, setRedemptions] = useState<PackageRedemption[]>([]);
  const [creditCents, setCreditCents] = useState(0);
  /** "I think if the CC is on file we can do the charging at the desk." */
  const [cardOnFile, setCardOnFile] = useState<{ label: string } | null>(null);
  /**
   * "can a client keep two different cards on file?"
   *
   * With two, the desk says which before charging - and a split bill takes
   * part on each without asking for either card again.
   */
  const [cards, setCards] = useState<{ id: string; label: string; is_default?: boolean; funding?: string | null }[]>([]);
  /* "I also put in for change for HSA cards" - and a press to take the fee
     off a sale where we cannot see what kind of card it is. */
  const [waiveCardFee, setWaiveCardFee] = useState(false);
  /**
   * "how do we charge different cards for example if the bill is 1000 and
   *  they want to pay 300 cash 200 on one card and 500 on another"
   *
   * Null is the ordinary sale: the press takes the whole bill. A number is
   * one slice of a split, and the sale stays open for the rest.
   */
  const [splitCents, setSplitCents] = useState<number | null>(null);
  const [cardId, setCardId] = useState<string | null>(null);
  /**
   * Tickets already sitting unpaid on this client's file.
   *
   * A payment link that was never paid leaves one behind, and nothing said so
   * - which is how Lauren Doroski ended up with three $229 tickets for one
   * treatment, two of them wrong.
   */
  const [openTickets, setOpenTickets] = useState<{ id: string; number: string | null; total_cents: number; created_at: string }[]>([]);
  const [useCredit, setUseCredit] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [appointment, setAppointment] = useState<VisitAppointment | null>(null);

  // Totals inputs
  const [discountStr, setDiscountStr] = useState("");
  const [discountPct, setDiscountPct] = useState<number | null>(null);
  const [discountReason, setDiscountReason] = useState("");
  const [tipCents, setTipCents] = useState(0);
  const [tipStr, setTipStr] = useState("");
  const [giftCodeStr, setGiftCodeStr] = useState("");
  const [giftLookupBusy, setGiftLookupBusy] = useState(false);
  const [appliedGiftCard, setAppliedGiftCard] = useState<AppliedGiftCard | null>(null);

  // Options
  const [emailReceipt, setEmailReceipt] = useState(true);
  /** Open the payment page here to key a card in, rather than texting it. */
  const [openPayHere, setOpenPayHere] = useState(false);
  /** Which line's discount is being set, if any. */
  const [discounting, setDiscounting] = useState<string | null>(null);
  const [markCompleted, setMarkCompleted] = useState(true);

  // Submission / result
  const [submitting, setSubmitting] = useState<PayMethod | null>(null);
  const [result, setResult] = useState<CheckoutResult | null>(null);
  /** Readers paired to her account. Empty until one is set up in Settings. */
  const [readers, setReaders] = useState<PairedReader[]>([]);
  /** The tap in progress: the ticket is made, the money is not in yet. */
  const [collecting, setCollecting] = useState<
    { invoiceId: string; number: string; readerId: string; totalCents: number; error: string | null } | null
  >(null);

  // Pickers
  const [picker, setPicker] = useState<"service" | "product" | "package" | "membership" | "gift_card" | "custom" | null>(null);

  /* Load config + catalogs once. */
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get<PosConfig>("/pos/config"),
      api.get<{ services: Service[] }>("/services"),
      api.get<{ products: Product[] }>("/payments/products"),
      api.get<{ packages: SpaPackage[] }>("/pos/packages"),
      // Memberships are optional at the register — never block the till on them.
      api.get<{ memberships: MembershipRow[] }>("/pos/memberships").catch(() => ({ memberships: [] as MembershipRow[] })),
    ])
      .then(([cfg, svc, prod, pkg, mem]) => {
        if (cancelled) return;
        setConfig(cfg);
        setCardKey(cfg.card_key ?? null);
        setServices(svc.services);
        setProducts(prod.products);
        setPackages(pkg.packages);
        setMemberships(mem.memberships.map(toMembership));
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errMessage(e));
      });
    return () => { cancelled = true; };
  }, []);

  /**
   * Has the practice started setting a reader up?
   *
   *   "Matt how do I use this terminal? I have no way to use the terminal
   *    card present"
   *
   * She had done step one - the address the reader lives at - and the reader
   * was sitting on the desk showing its three-word pairing code, but checkout
   * says nothing at all until a reader is paired, so there was nothing to
   * follow. Once somewhere has been set up, say where the last step is.
   */
  const [readerPlace, setReaderPlace] = useState(false);

  /* Which readers are paired, and are they awake?
   *
   * Asked on open and again every half minute, because a reader that went to
   * sleep or dropped off the wifi should grey its own button out rather than
   * fail at the moment somebody is standing there with a card. Never an error
   * on this screen: no reader simply means no button. */
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get<{ readers: PairedReader[]; location?: { id: string } | null }>("/pos/terminal")
        .then((d) => {
          if (cancelled) return;
          setReaders(d.readers ?? []);
          setReaderPlace(!!d.location);
        })
        .catch(() => { if (!cancelled) { setReaders([]); setReaderPlace(false); } });
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  /* Load the appointment from ?appointment_id=. */
  useEffect(() => {
    if (!appointmentId) { setAppointment(null); return; }
    let cancelled = false;
    api.get<{ appointment: VisitAppointment }>(`/appointments/${appointmentId}`)
      .then((d) => { if (!cancelled) setAppointment(d.appointment); })
      .catch((e) => { if (!cancelled) toast.error("Couldn't load appointment", errMessage(e)); });
    return () => { cancelled = true; };
    // toast is stable (context value) — intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  /* Prefill a client (and optionally a membership line) from the URL. */
  const prefilledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!prefillContactId || appointmentId || !memberships) return;
    const key = `${prefillContactId}:${prefillMembershipId ?? ""}`;
    if (prefilledRef.current === key) return;
    prefilledRef.current = key;
    api.get<{ contact: Contact }>(`/contacts/${prefillContactId}`)
      .then((d) => {
        setClient({ id: d.contact.id, name: fullName(d.contact), email: d.contact.email, phone: d.contact.phone });
        const m = prefillMembershipId ? memberships.find((x) => x.id === prefillMembershipId) : undefined;
        if (m) {
          setLines((prev) => prev.some((l) => l.kind === "membership") ? prev : [...prev, {
            uid: uid(), kind: "membership", refId: m.id, name: m.name,
            caption: `Renews ${everyCycle(m.interval)}`,
            unitCents: m.price_cents, qty: 1, taxable: false, unitOverride: false, redeemBalanceId: null,
          }]);
        }
      })
      .catch((e) => toast.error("Couldn't load that client", errMessage(e)));
    // toast is stable (context value) — intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillContactId, prefillMembershipId, appointmentId, memberships]);

  /**
   * Put somebody else's visit on this bill.
   *
   *   "how do I combine clients at checkout - for one bill but two separate
   *    orders"
   *
   * Their treatments join the ticket tagged with their name, and paying
   * closes their visit too. Their own wallet is left alone: a voucher
   * belongs to the person it was bought for.
   */
  const addPersonToBill = useCallback(async (c: Contact) => {
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "Client";
    try {
      const d = await api.get<{ appointments: VisitAppointment[] }>("/appointments", {
        contact_id: c.id, limit: 5,
      });
      const today = (d.appointments ?? []).find((a) =>
        !["cancelled", "no_show", "completed"].includes(a.status)) ?? (d.appointments ?? [])[0] ?? null;
      const segs: ApptSegment[] = today?.segments?.length
        ? today.segments
        : today?.service_id
          ? [{ service_id: today.service_id, service_name: today.service_name ?? null,
               price_cents: today.price_cents, provider_name: null, room_name: null,
               starts_at: today.starts_at }]
          : [];
      const theirs: Line[] = segs.filter((seg) => !!seg.service_id).map((seg) => {
        const svc = services?.find((x) => x.id === seg.service_id);
        return {
          uid: uid(),
          kind: "service" as const,
          refId: seg.service_id,
          name: seg.service_name ?? svc?.name ?? "Treatment",
          caption: `for ${name}`,
          unitCents: seg.price_cents ?? svc?.price_cents ?? 0,
          qty: 1,
          taxable: svc ? svc.taxable : true,
          unitOverride: true,
          redeemBalanceId: null,
          forName: name,
          forContactId: c.id,
        };
      });
      setAlsoFor((prev) => prev.some((p) => p.contact_id === c.id)
        ? prev
        : [...prev, { contact_id: c.id, appointment_id: today?.id ?? null, name }]);
      if (theirs.length > 0) setLines((prev) => [...prev, ...theirs]);
      toast.success(
        `${name} is on this bill`,
        theirs.length > 0
          ? `${theirs.length} treatment${theirs.length === 1 ? "" : "s"} added. Their visit closes when this is paid.`
          : "Nothing booked for them today, so add what they had by hand.",
      );
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setAddingPerson(false);
    }
  // services + toast are stable enough for this one-shot action.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  /* Every service performed at this visit, in order — a multi-service visit
     carries `segments`; older single-service appointments don't. */
  const apptSegments = useMemo<ApptSegment[]>(() => {
    if (!appointment) return [];
    if (appointment.segments && appointment.segments.length > 0) return appointment.segments;
    if (!appointment.service_id) return [];
    return [{
      service_id: appointment.service_id,
      service_name: appointment.service_name ?? null,
      price_cents: appointment.price_cents,
      provider_name: appointment.provider_name ?? null,
      room_name: appointment.room_name ?? null,
      starts_at: appointment.starts_at,
    }];
  }, [appointment]);

  /** "Consultation + Hydration Drip" — everything she's ringing up in one go. */
  const visitServiceLabel = useMemo(() => {
    const names = apptSegments.map((s) => s.service_name).filter((n): n is string => !!n);
    if (names.length > 0) return names.join(" + ");
    return appointment?.service_name ?? appointment?.title ?? "";
  }, [apptSegments, appointment]);

  /** Every provider who touched the visit, deduped and in order. */
  const visitProviders = useMemo(() => {
    const names: string[] = [];
    for (const seg of apptSegments) {
      if (seg.provider_name && !names.includes(seg.provider_name)) names.push(seg.provider_name);
    }
    if (names.length === 0 && appointment?.provider_name) names.push(appointment.provider_name);
    return names.join(", ");
  }, [apptSegments, appointment]);

  /* Seed the ticket from the appointment once both it and the catalog have
     loaded: preselect the client, add one line per service performed, each at
     its own price. Runs once per appointment id. */
  const seededApptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!appointment || !services) return;
    if (seededApptRef.current === appointment.id) return;
    seededApptRef.current = appointment.id;

    // A segment whose service was deleted has no id to ring up — skip it
    // rather than posting a null service_id and failing the whole sale.
    const seeded: Line[] = apptSegments.filter((seg) => !!seg.service_id).flatMap((seg) => {
      const svc = services.find((s) => s.id === seg.service_id);
      const line: Line = {
        uid: uid(),
        kind: "service" as const,
        refId: seg.service_id,
        name: seg.service_name ?? svc?.name ?? appointment.title,
        caption: [svc?.category_name, seg.provider_name].filter(Boolean).join(" · ") || undefined,
        unitCents: seg.price_cents ?? svc?.price_cents ?? 0,
        qty: 1,
        taxable: svc ? svc.taxable : true,
        unitOverride: true,
        redeemBalanceId: null,
      };
      // Whatever went into the bag is charged too, at the price it carried
      // when it was added.
      const extras: Line[] = (seg.addons ?? []).map((a) => {
        const addonSvc = services.find((x) => x.id === a.service_id);
        return {
          uid: uid(),
          kind: "service" as const,
          refId: a.service_id,
          name: a.name,
          caption: `Added to ${seg.service_name ?? svc?.name ?? "the visit"}`,
          unitCents: a.price_cents,
          qty: 1,
          taxable: addonSvc ? addonSvc.taxable : true,
          unitOverride: true,
          redeemBalanceId: null,
        };
      });
      return [line, ...extras];
    });
    if (seeded.length > 0) setLines((prev) => [...prev, ...seeded]);

    if (appointment.contact_id) {
      const cid = appointment.contact_id;
      // The appointment row doesn't carry the contact's email — fetch it so
      // the receipt checkbox knows whether there's an address on file.
      api.get<{ contact: Contact }>(`/contacts/${cid}`)
        .then((d) => setClient({
          id: d.contact.id,
          name: fullName(d.contact),
          email: d.contact.email,
          phone: d.contact.phone,
        }))
        .catch(() => setClient({ id: cid, name: appointment.contact_name ?? "Client", email: null, phone: null }));
    }
  }, [appointment, apptSegments, services]);

  /* Package balances, package usage history, and account credit all follow the
     selected client. */
  useEffect(() => {
    if (!client) {
      setBalances([]);
      setRedemptions([]);
      setCreditCents(0);
      setUseCredit(false);
      setCardOnFile(null);
      setOpenTickets([]);
      return;
    }
    let cancelled = false;
    const contactId = client.id;
    setUseCredit(false);
    // Practice sales only ever use what practice sold, and real sales only
    // what was really sold — so a client's real packages never move in practice.
    api.get<{ balances: PackageBalance[] }>("/pos/package-balances", { contact_id: contactId })
      .then((d) => { if (!cancelled) setBalances(d.balances.filter((b) => (b.practice === true) === practiceOn)); })
      .catch(() => { if (!cancelled) setBalances([]); });
    api.get<{ redemptions: PackageRedemption[] }>("/pos/package-redemptions", { contact_id: contactId })
      .then((d) => { if (!cancelled) setRedemptions(d.redemptions); })
      .catch(() => { if (!cancelled) setRedemptions([]); });
    api.get<{ balance_cents: number }>("/pos/credit", { contact_id: contactId, ...(practiceOn ? { practice: "1" } : {}) })
      .then((d) => { if (!cancelled) setCreditCents(Number(d.balance_cents) || 0); })
      .catch(() => { if (!cancelled) setCreditCents(0); });
    api.get<{ card: { label: string } | null; cards?: { id: string; label: string; is_default?: boolean; funding?: string | null }[] }>(
      `/frontdesk/clients/${contactId}/card`)
      .then((d) => {
        if (cancelled) return;
        setCardOnFile(d.card ? { label: d.card.label } : null);
        const list = d.cards ?? [];
        setCards(list);
        setCardId(list.find((c) => c.is_default)?.id ?? list[0]?.id ?? null);
      })
      .catch(() => { if (!cancelled) { setCardOnFile(null); setCards([]); setCardId(null); } });
    api.get<{ invoices: { id: string; number: string | null; status: string; total_cents: number; created_at: string }[] }>(
      "/payments/invoices", { contact_id: contactId, limit: 20 })
      .then((d) => {
        if (cancelled) return;
        setOpenTickets(d.invoices
          .filter((i) => i.status === "open" && Number(i.total_cents) > 0)
          .map((i) => ({ id: i.id, number: i.number, total_cents: Number(i.total_cents), created_at: i.created_at })));
      })
      .catch(() => { if (!cancelled) setOpenTickets([]); });
    return () => { cancelled = true; };
  }, [client?.id, practiceOn]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Clearing the client invalidates package redemptions, package purchases,
     memberships, and their account credit. */
  const clearClient = useCallback(() => {
    setClient(null);
    setLines((prev) => prev
      .filter((l) => l.kind !== "package" && l.kind !== "membership")
      .map((l) => (l.redeemBalanceId ? { ...l, redeemBalanceId: null } : l)));
  }, []);

  /* ── Line ops ── */

  const addLine = useCallback((line: Omit<Line, "uid">) => {
    setLines((prev) => {
      // Adding the same catalog item again bumps qty instead of duplicating.
      if (line.refId && (line.kind === "service" || line.kind === "product" || line.kind === "package")) {
        const existing = prev.find((l) => l.kind === line.kind && l.refId === line.refId && !l.redeemBalanceId && !l.unitOverride);
        if (existing) return prev.map((l) => (l.uid === existing.uid ? { ...l, qty: l.qty + 1 } : l));
      }
      // A membership is a subscription, not a quantity — one line, once.
      if (line.kind === "membership" && prev.some((l) => l.kind === "membership" && l.refId === line.refId)) {
        return prev;
      }
      return [...prev, { ...line, uid: uid() }];
    });
  }, []);

  const removeLine = useCallback((id: string) => {
    setLines((prev) => prev.filter((l) => l.uid !== id));
  }, []);

  const stepQty = useCallback((id: string, delta: number) => {
    setLines((prev) => prev.map((l) => (l.uid === id ? { ...l, qty: Math.max(1, l.qty + delta) } : l)));
  }, []);

  const setFulfilment = useCallback((lineId: string, fulfilment: Fulfilment) => {
    setLines((prev) => prev.map((l) => (l.uid === lineId ? { ...l, fulfilment } : l)));
  }, []);

  const toggleRedeem = useCallback((lineId: string, balanceId: string) => {
    setLines((prev) => prev.map((l) =>
      l.uid === lineId ? { ...l, redeemBalanceId: l.redeemBalanceId ? null : balanceId } : l
    ));
  }, []);

  /** Sessions still available on a balance after subtracting what other ticket
      lines already redeem against it. */
  const remainingFor = useCallback((balance: PackageBalance, excludeLineId?: string): number => {
    const used = lines.reduce((s, l) =>
      s + (l.redeemBalanceId === balance.id && l.uid !== excludeLineId ? l.qty : 0), 0);
    return balance.sessions_remaining - used;
  }, [lines]);

  /**
   * The voucher a line can be covered by (an already-chosen one wins).
   *
   * Treatments and products are kept apart: a product voucher carries no
   * treatment, so without this a drip line would happily offer to spend the
   * client's Hair Renew.
   */
  const balanceForLine = useCallback((l: Line): PackageBalance | undefined => {
    if (l.kind !== "service" && l.kind !== "product") return undefined;
    if (l.redeemBalanceId) return balances.find((b) => b.id === l.redeemBalanceId);
    if (l.kind === "product") {
      return balances.find((b) =>
        b.sessions_remaining > 0 && b.product_id === l.refId && remainingFor(b) > 0
      );
    }
    return balances.find((b) =>
      b.sessions_remaining > 0 && !b.product_id &&
      (b.service_id === l.refId || b.service_id == null) &&
      remainingFor(b) > 0
    );
  }, [balances, remainingFor]);

  /* ── Totals (client-side estimate; the server recomputes authoritatively) ── */

  const taxRate = config?.tax_rate_pct ?? 0;
  const discountCents = parseDollars(discountStr);

  const totals = useMemo(() => {
    // A line's own discount comes off before anything else, exactly as the
    // server applies it, so the screen and the charge agree.
    const effective = (l: Line) => (l.redeemBalanceId
      ? 0
      : Math.max(0, l.unitCents * l.qty - (l.discountCents ?? 0)));
    const subtotal = lines.reduce((s, l) => s + effective(l), 0);
    const discount = Math.min(discountCents, subtotal);
    const taxableAmt = lines.reduce((s, l) => s + (l.taxable ? effective(l) : 0), 0);
    // Discount reduces the taxable base pro-rata across taxable lines.
    const taxBase = subtotal > 0 ? Math.max(0, taxableAmt - Math.round((discount * taxableAmt) / subtotal)) : 0;
    const tax = Math.round((taxBase * taxRate) / 100);
    const preGift = subtotal - discount + tax + tipCents;
    const giftApplied = appliedGiftCard ? Math.min(appliedGiftCard.balance_cents, Math.max(preGift, 0)) : 0;
    // Account credit lands after the gift card, capped at what's still owed —
    // the same order the server applies them in.
    const afterGift = Math.max(preGift - giftApplied, 0);
    const creditApplied = useCredit ? Math.min(creditCents, afterGift) : 0;
    const total = Math.max(afterGift - creditApplied, 0);
    // Tip suggestions are a % of the full service value — a package-paid
    // facial still deserves a tip on what the service is worth.
    const serviceValue = lines.reduce((s, l) => s + (l.kind === "service" ? l.unitCents * l.qty : 0), 0);
    /**
     * What a card payment would add, worked out exactly as the server does.
     *
     * It is a PREVIEW, not a decision: the method is chosen by which button
     * she presses, so this shows what each of the card buttons would come to
     * and the Total above stays what they bought. The card on file is the one
     * case where the kind of card is knowable before the press, and a debit,
     * HSA or FSA card is never surcharged.
     */
    const feePct = Number(config?.card_fee_pct ?? 0);
    const chosen = cards.find((c) => c.id === cardId) ?? cards.find((c) => c.is_default) ?? cards[0];
    const noFeeCard = (chosen?.funding ?? "").toLowerCase();
    const feeApplies = feePct > 0 && !waiveCardFee && !(noFeeCard === "debit" || noFeeCard === "prepaid");
    // A split takes only part on this card, so the fee follows the part.
    const onCardNow = splitCents !== null && splitCents > 0 && splitCents < total ? splitCents : total;
    const cardFee = feeApplies ? Math.round((onCardNow * feePct) / 100) : 0;
    return { subtotal, discount, tax, giftApplied, creditApplied, total, serviceValue, cardFee, feePct, feeApplies };
  }, [lines, discountCents, taxRate, tipCents, appliedGiftCard, useCredit, creditCents, config, cards, cardId, waiveCardFee, splitCents]);

  /* ── Gift card apply ── */

  const applyGiftCode = useCallback(async () => {
    const code = giftCodeStr.trim();
    if (!code) return;
    setGiftLookupBusy(true);
    try {
      const d = await api.get<{ gift_card: { id: string; code: string; initial_cents: number; balance_cents: number; active: boolean } }>(
        "/pos/gift-cards/lookup",
        { code }
      );
      if (!d.gift_card.active) { toast.error("Gift card is disabled"); return; }
      if (d.gift_card.balance_cents <= 0) { toast.error("Gift card has no balance left"); return; }
      setAppliedGiftCard({ id: d.gift_card.id, code: d.gift_card.code, balance_cents: d.gift_card.balance_cents });
      setGiftCodeStr("");
    } catch (e) {
      toast.error("Gift card not found", errMessage(e));
    } finally {
      setGiftLookupBusy(false);
    }
  }, [giftCodeStr, toast]);

  /* ── Submit ── */

  /* The one reader to send this ticket to: paired, awake, and not already
     mid-tap for somebody else. She has one; the shape allows for more. */
  const liveReader = useMemo(
    () => readers.find((r) => r.status === "online" && !r.busy) ?? null,
    [readers],
  );

  /* `submit` resubmits itself once when the duplicate warning is accepted, and
     a useCallback can't name itself in its own dependency list. */
  const submitRef = useRef<((m: PayMethod, confirmDuplicate?: boolean) => Promise<void>) | null>(null);

  const submit = useCallback(async (
    method: PayMethod, confirmDuplicate = false,
    /** A card she typed in here: the token for it, and whether to keep it. */
    typed?: { paymentMethodId: string; save: boolean },
  ) => {
    // `submitting` here is this render's value, and the duplicate retry comes
    // from inside a submit that is still marked in flight - so the retry
    // deliberately walks past the double-press guard.
    if (lines.length === 0 || (submitting && !confirmDuplicate)) return;
    /**
     * "It says paid on the machine but it did not pay it."
     *
     * The card-machine button is the one press on this screen that writes down
     * money without taking any, so it asks first - in the words of what it
     * actually does, not "are you sure".
     */
    if (method === "card_external" && !confirmDuplicate) {
      const ok = await confirm({
        title: `Did ${formatMoney(totals.total)} already go through?`,
        description: (
          <>
            <p>
              This writes the sale down as paid. It does not charge anything - the money has to
              have already gone through on your own card machine.
            </p>
            <p className="mt-2 text-zinc-400">
              If you are trying to take the money now, use the card on file or text them a
              payment link instead.
            </p>
          </>
        ),
        confirmLabel: "Yes, it went through",
      });
      if (!ok) return;
    }
    setSubmitting(method);
    try {
      const items = lines.map((l) => {
        const off = {
          ...(l.discountCents && l.discountCents > 0
            ? { discount_cents: l.discountCents, ...(l.discountReason?.trim() ? { discount_reason: l.discountReason.trim() } : {}) }
            : {}),
          /* Whose treatment it is, when it is not the person paying. Goes on
             every kind of line, so a product bought for somebody else on the
             same bill lands on their record too. */
          ...(l.forContactId ? { for_contact_id: l.forContactId } : {}),
        };
        switch (l.kind) {
          case "service":
            return {
              kind: "service" as const,
              service_id: l.refId,
              qty: l.qty,
              ...(l.unitOverride ? { unit_amount_cents: l.unitCents } : {}),
              ...(l.redeemBalanceId ? { redeem_package_balance_id: l.redeemBalanceId } : {}),
              ...off,
            };
          case "product":
            return {
              kind: "product" as const,
              product_id: l.refId,
              qty: l.qty,
              ...(l.redeemBalanceId ? { redeem_package_balance_id: l.redeemBalanceId } : {}),
              ...off,
            };
          case "package":
            return { kind: "package" as const, package_id: l.refId, qty: l.qty, fulfilment: l.fulfilment ?? "in_lounge", ...off };
          case "membership":
            return { kind: "membership" as const, membership_id: l.refId, ...off };
          case "gift_card":
            return {
              kind: "gift_card" as const,
              amount_cents: l.unitCents,
              ...(l.recipientName ? { recipient_name: l.recipientName } : {}),
              ...(l.giftCode ? { code: l.giftCode } : {}),
              ...(l.forContactId ? { for_contact_id: l.forContactId } : {}),
            };
          case "custom":
            return { kind: "custom" as const, name: l.name, qty: l.qty, unit_amount_cents: l.unitCents, taxable: l.taxable, ...off };
        }
      });
      const receipt = emailReceipt && !!client?.email;
      const d = await api.post<{
        ok: boolean;
        invoice_id: string;
        number: string;
        total_cents: number;
        status: "paid" | "open" | "collecting" | "partly_paid";
        balance_cents?: number;
        reader_id?: string;
        gift_card_applied_cents: number;
        credit_applied_cents: number;
        pay_url?: string | null;
        auto_renews?: boolean;
        practice?: boolean;
        charged_card?: string | null;
      }>("/pos/checkout", {
        contact_id: client?.id ?? null,
        appointment_id: appointment?.id ?? null,
        ...(alsoFor.length
          ? { also_for: alsoFor.map((p) => ({ contact_id: p.contact_id, appointment_id: p.appointment_id })) }
          : {}),
        items,
        tip_cents: tipCents,
        discount_cents: totals.discount,
        discount_reason: discountReason || undefined,
        ...(appliedGiftCard ? { gift_card_code: appliedGiftCard.code } : {}),
        ...(useCredit && creditCents > 0 ? { use_account_credit: true } : {}),
        payment_method: method,
        receipt,
        ...(method === "card_terminal" && liveReader ? { reader_id: liveReader.id } : {}),
        ...(method === "card_on_file" && cardId ? { card_id: cardId } : {}),
        ...(method === "card_manual" && typed
          ? { payment_method_id: typed.paymentMethodId, save_card: typed.save } : {}),
        ...(confirmDuplicate ? { confirm_duplicate: true } : {}),
        ...(waiveCardFee ? { waive_card_fee: true } : {}),
        ...(appointment ? { mark_appointment_completed: markCompleted } : {}),
        // "300 cash 200 on one card and 500 on another" - take only this
        // slice now and leave the rest owing on the same ticket.
        ...(splitCents !== null && splitCents > 0 && splitCents < totals.total
          ? { amount_cents: splitCents } : {}),
      });
      /* The reader has the amount on it and the client has not tapped yet.
         The ticket exists but is NOT paid, so the paid screen would be a lie;
         the waiting screen takes over until the card goes through. */
      if (d.status === "collecting") {
        setCollecting({
          invoiceId: d.invoice_id, number: d.number, totalCents: d.total_cents,
          readerId: d.reader_id ?? liveReader?.id ?? "", error: null,
        });
        return;
      }
      setResult({
        invoice_id: d.invoice_id,
        number: d.number,
        total_cents: d.total_cents,
        status: d.status,
        charged_card: d.charged_card ?? null,
        gift_card_applied_cents: d.gift_card_applied_cents,
        credit_applied_cents: d.credit_applied_cents ?? 0,
        pay_url: d.pay_url ?? undefined,
        auto_renews: d.auto_renews,
        practice: d.practice === true,
        method,
        receiptEmail: receipt && !d.practice ? client?.email ?? null : null,
        balance_cents: typeof d.balance_cents === "number" ? d.balance_cents : undefined,
      });
      if (d.practice) onPracticeSale();
      // She asked for the page here, so open it rather than making her hunt
      // for the small icon on the next screen.
      if (openPayHere && d.pay_url) window.open(d.pay_url, "_blank", "noopener");
    } catch (e) {
      /**
       * The same client, the same money, twice inside two hours.
       *
       * "I could not check out Lauren Dorskie but it say checked out" - one
       * $229 treatment became three tickets, because each attempt looked like
       * it had failed. The server stops the second one; this is where she
       * either sees her mistake or says she meant it.
       */
      if (e instanceof ApiError && e.status === 409
          && (e.data as { code?: string } | undefined)?.code === "possible_duplicate") {
        setSubmitting(null);
        const ok = await confirm({
          title: "Charge this again?",
          description: (
            <>
              <p>{e.message}</p>
              <p className="mt-2 text-zinc-400">
                If the last try looked like it failed, check Sales first - it may have gone
                through.
              </p>
            </>
          ),
          confirmLabel: "Yes, charge again",
          danger: true,
        });
        if (ok) await submitRef.current?.(method, true);
        return;
      }
      toast.error("Checkout failed", errMessage(e));
    } finally {
      setSubmitting(null);
    }
  }, [lines, submitting, client, appointment, tipCents, totals.discount, totals.total, appliedGiftCard, useCredit, creditCents, emailReceipt, markCompleted, toast, confirm, onPracticeSale, openPayHere, splitCents, liveReader]);
  submitRef.current = submit;

  /**
   * Watch the tap.
   *
   * The server owns this: it made the charge and handed it to the reader, so
   * it is the one that knows whether the money arrived. Asking every second
   * is plenty for somebody holding a card, and it means a browser that
   * reloads mid-tap picks the same answer up again.
   */
  useEffect(() => {
    if (!collecting || collecting.error) return;
    let stop = false;
    const tick = async () => {
      try {
        const d = await api.get<{ status: "collecting" | "paid" | "failed" | "cancelled"; error?: string; card?: string | null }>(
          `/pos/terminal/collect/${collecting.invoiceId}`);
        if (stop) return;
        if (d.status === "paid") {
          setCollecting(null);
          setResult({
            invoice_id: collecting.invoiceId, number: collecting.number,
            total_cents: collecting.totalCents, status: "paid",
            gift_card_applied_cents: 0, credit_applied_cents: 0,
            charged_card: d.card ?? null, method: "card_terminal",
            receiptEmail: emailReceipt && client?.email ? client.email : null,
          });
          return;
        }
        if (d.status === "failed") { setCollecting((c) => (c ? { ...c, error: d.error ?? "The card was declined" } : c)); return; }
        if (d.status === "cancelled") { setCollecting(null); return; }
      } catch { /* a dropped poll is not a failed sale - ask again */ }
      if (!stop) timer = setTimeout(() => void tick(), 1000);
    };
    let timer = setTimeout(() => void tick(), 600);
    return () => { stop = true; clearTimeout(timer); };
  }, [collecting, emailReceipt, client?.email]);

  const cancelCollection = useCallback(async () => {
    if (!collecting) return;
    const c = collecting;
    setCollecting(null);
    try {
      await api.post(`/pos/terminal/cancel/${c.invoiceId}`, { reader_id: c.readerId });
      toast.success("Stopped", "The reader is clear and the ticket is back as it was.");
    } catch (e) {
      toast.error("Couldn't stop it cleanly", errMessage(e));
    }
  }, [collecting, toast]);

  const retryCollection = useCallback(async () => {
    if (!collecting) return;
    setCollecting((c) => (c ? { ...c, error: null } : c));
    try {
      await api.post(`/pos/terminal/retry/${collecting.invoiceId}`, { reader_id: collecting.readerId });
    } catch (e) {
      setCollecting((c) => (c ? { ...c, error: errMessage(e) } : c));
    }
  }, [collecting]);

  /**
   * Pull a saved ticket back up at the till.
   *
   * It reads what is on it and what is left owing, then hands it to the same
   * screen a part-paid sale uses, so the money is taken the ordinary way -
   * cash, the reader, a card on file, or split across them.
   */
  const openHeldTicket = useCallback(async (invoiceId: string) => {
    try {
      const b = await api.get<{
        invoice_id: string; number: string | null; status: string;
        total_cents: number; paid_cents: number; balance_cents: number;
      }>(`/pos/invoices/${invoiceId}/balance`);
      setResult({
        invoice_id: b.invoice_id,
        number: b.number ?? "",
        total_cents: b.total_cents,
        // "none" is what marks this as a ticket she saved rather than a sale
        // that has just been taken, so the screen opens on the balance.
        status: b.paid_cents > 0 ? "partly_paid" : "open",
        method: "none",
        gift_card_applied_cents: 0,
        credit_applied_cents: 0,
        receiptEmail: emailReceipt && client?.email ? client.email : null,
        balance_cents: b.balance_cents,
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open that ticket");
    }
  }, [emailReceipt, client?.email, toast]);

  const resetSale = useCallback(() => {
    setResult(null);
    setLines([]);
    setClient(null);
    setBalances([]);
    setRedemptions([]);
    setCreditCents(0);
    setUseCredit(false);
    setAppointment(null);
    seededApptRef.current = null;
    setDiscountStr("");
    setDiscountPct(null);
    setDiscountReason("");
    setTipCents(0);
    setTipStr("");
    setGiftCodeStr("");
    setAppliedGiftCard(null);
    setEmailReceipt(true);
    setMarkCompleted(true);
    navigate("/checkout", { replace: true });
  }, [navigate]);

  /* ── Render ── */

  if (loadError) {
    return (
      <EmptyState
        icon={X}
        title="Couldn't load the register"
        description={loadError}
        action={<Button variant="secondary" onClick={() => window.location.reload()}>Retry</Button>}
      />
    );
  }

  const loading = !config || !services || !products || !packages || !memberships;
  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  /**
   * The client is at the reader.
   *
   * Full screen on purpose: there is a card in somebody's hand and exactly one
   * thing to do, so nothing else on the till should be clickable. It says the
   * amount, because the reader says the amount, and the two agreeing is the
   * whole point of this button existing.
   */
  if (collecting) {
    return (
      <div className="mx-auto max-w-md pt-10 text-center">
        <div className={cn("rounded-2xl border p-8",
          collecting.error ? "border-rose-500/40 bg-rose-500/5" : "border-indigo-500/40 bg-indigo-500/5")}>
          {collecting.error ? (
            <>
              <X className="mx-auto h-10 w-10 text-rose-300" />
              <h2 className="mt-4 text-lg font-semibold text-zinc-100">That card didn&rsquo;t go through</h2>
              <p className="mt-1 text-sm text-rose-200">{collecting.error}</p>
              <p className="mt-3 text-sm text-zinc-400">
                Nothing has been taken. Try the same card again, or stop and take it another way.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-indigo-300" />
              <h2 className="mt-4 text-lg font-semibold text-zinc-100">
                Tap, insert or swipe on the reader
              </h2>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-100">
                {formatMoney(collecting.totalCents)}
              </p>
              <p className="mt-3 text-sm text-zinc-400">
                It is already on the reader - nothing to type. This screen changes by itself the
                moment the card goes through.
              </p>
            </>
          )}
          <p className="mt-4 text-xs text-zinc-600">{collecting.number}</p>
          <div className="mt-6 flex justify-center gap-2">
            {collecting.error && (
              <Button onClick={() => void retryCollection()}>Try that card again</Button>
            )}
            <Button variant="secondary" onClick={() => void cancelCollection()}>
              {collecting.error ? "Stop and go back" : "Cancel"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (result && (result.status === "partly_paid" || (result.status === "open" && result.method === "none"))) {
    return <BalancePanel result={result} liveReader={liveReader} cards={cards}
      onPaid={(r) => setResult(r)} onNewSale={resetSale} />;
  }
  if (result) {
    return <ResultPanel result={result} onPaid={(r) => setResult(r)} onNewSale={resetSale} />;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
      {/* LEFT — the ticket */}
      <div className="space-y-4 min-w-0">
        {appointment && (
          <div className="flex items-center gap-2 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-4 py-2.5 text-sm text-indigo-200">
            <Sparkles className="h-4 w-4 shrink-0" />
            <span className="truncate">
              Checking out: <span className="font-medium">{visitServiceLabel}</span>
              {visitProviders && <> with {visitProviders}</>}
              <span className="text-indigo-300/70"> · {formatDate(appointment.starts_at)} {formatTime(appointment.starts_at)}</span>
            </span>
          </div>
        )}

        <ClientCard
          client={client}
          balances={balances}
          redemptions={redemptions}
          onSelect={setClient}
          onClear={clearClient}
          alsoFor={alsoFor}
          onAddPerson={client ? () => setAddingPerson(true) : undefined}
          onDropPerson={(id) => {
            const gone = alsoFor.find((p) => p.contact_id === id);
            setAlsoFor((prev) => prev.filter((p) => p.contact_id !== id));
            if (gone) setLines((prev) => prev.filter((l) => l.forName !== gone.name));
          }}
        />

        {/* "I could not check out Lauren Dorskie but it say checked out." A
            payment link that was never paid leaves an unpaid ticket behind,
            and nothing used to say so - so the next attempt made a second one.
            Say it, before she rings the same treatment up again. */}
        {/*
            "I need a save button at checkout when we add items or services to
             be ready when they come to check out"

            Saving one was always possible; getting back to it was not. This
            used to point at the drawer page and say "check Sales", which is
            not where the ticket is, so the only way on was to ring the whole
            thing up again - three $229 tickets for one treatment. Each held
            ticket now opens where the money is taken.
        */}
        {openTickets.length > 0 && !result && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-100">
            <p>
              {client?.name.split(" ")[0] ?? "This client"} already has{" "}
              {openTickets.length === 1 ? "a saved ticket" : `${openTickets.length} saved tickets`} waiting
              {openTickets.length > 1 && (
                <> totalling {formatMoney(openTickets.reduce((t, o) => t + o.total_cents, 0))}</>
              )}. Open it rather than ringing the same thing up again.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {openTickets.map((t) => (
                <Button
                  key={t.id}
                  size="sm"
                  variant="secondary"
                  onClick={() => void openHeldTicket(t.id)}
                >
                  Open {t.number ?? "ticket"} - {formatMoney(t.total_cents)} from {formatTime(t.created_at)}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className={CARD_CLASS}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-zinc-200">Ticket</h2>
            <div className="flex items-center gap-2">
              {lines.length > 0 && (
                <span className="text-xs text-zinc-500">{lines.length} {lines.length === 1 ? "item" : "items"}</span>
              )}
              {/*
                "I can add the service at checkout but it won't save till he
                 is ready / I need a save button at checkout when we add items
                 or services to be ready when they come to check out"

                The hold always existed, at the bottom of the payment buttons
                called "Save unpaid", which reads as a verdict on the money
                rather than a way to put the ticket down. It belongs here,
                beside the things she is adding.
              */}
              {lines.length > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={submitting !== null}
                  loading={submitting === "none"}
                  onClick={() => void submit("none")}
                >
                  Save for later
                </Button>
              )}
            </div>
          </div>

          {lines.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-800 px-4 py-6 text-center text-sm text-zinc-500">
              Nothing on the ticket yet — add a service, retail item, or package below.
            </p>
          ) : (
            <ul className="divide-y divide-zinc-800/70">
              {lines.map((l) => {
                const bal = balanceForLine(l);
                const redeemed = !!l.redeemBalanceId;
                const remaining = bal ? remainingFor(bal, l.uid) : 0;
                return (
                  <li key={l.uid} className="py-2.5">
                   <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm text-zinc-200">{l.name}</span>
                        {redeemed && <Badge color="violet" size="xs">Package</Badge>}
                        {l.kind === "membership" && <Badge color="violet" size="xs">Membership</Badge>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2">
                        {l.kind === "package" && l.fulfilment === "take_home" ? (
                          <span className="text-xs text-zinc-500">
                            All {(l.sessions ?? 0) * l.qty || ""} doses handed over today
                          </span>
                        ) : l.kind === "package" && l.fulfilment === "mail_out" ? (
                          <span className="text-xs text-zinc-500">
                            Mailed direct to the client today — no vouchers to come back for
                          </span>
                        ) : (
                          l.caption && <span className="text-xs text-zinc-500">{l.caption}</span>
                        )}
                        {/* Asked on EVERY package, not only one ticked as
                            take-home, and all three of her answers: "at
                            checkout it did not ask me how I was selling". */}
                        {l.kind === "package" && (
                          <span className="inline-flex overflow-hidden rounded-full ring-1 ring-inset ring-zinc-700" role="group" aria-label="How the client is getting this package">
                            {FULFILMENTS.map((o) => (
                              <button
                                key={o.v}
                                type="button"
                                onClick={() => setFulfilment(l.uid, o.v)}
                                aria-pressed={(l.fulfilment ?? "in_lounge") === o.v}
                                className={cn(
                                  "px-2 py-0.5 text-[11px] font-medium",
                                  (l.fulfilment ?? "in_lounge") === o.v
                                    ? "bg-teal-500/15 text-teal-300"
                                    : "text-zinc-500 hover:text-zinc-200"
                                )}
                              >
                                {o.label}
                              </button>
                            ))}
                          </span>
                        )}
                        {/* Her per-line discount: "each service or product
                            needs the option to discount with options why".
                            A voucher-paid line is already free, so it is not
                            offered there. */}
                        {!redeemed && l.kind !== "gift_card" && (
                          <button
                            type="button"
                            onClick={() => setDiscounting(l.uid)}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
                              (l.discountCents ?? 0) > 0
                                ? "bg-amber-500/15 text-amber-300 ring-amber-500/30"
                                : "bg-zinc-800/70 text-zinc-400 ring-zinc-700 hover:text-zinc-200"
                            )}
                          >
                            {(l.discountCents ?? 0) > 0
                              ? <>-{formatMoney(l.discountCents!)}{l.discountReason ? ` · ${l.discountReason}` : ""}</>
                              : <>Discount</>}
                          </button>
                        )}
                        {(l.kind === "service" || l.kind === "product") && bal && (redeemed || remaining > 0) && (
                          <button
                            type="button"
                            onClick={() => toggleRedeem(l.uid, bal.id)}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
                              redeemed
                                ? "bg-violet-500/15 text-violet-300 ring-violet-500/30"
                                : "bg-zinc-800/70 text-zinc-400 ring-zinc-700 hover:text-zinc-200"
                            )}
                            title={bal.package_name ?? "Package"}
                          >
                            {redeemed ? (
                              <><Check className="h-3 w-3" /> Using package</>
                            ) : (
                              <>Use package ({remaining} left)</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {l.kind !== "gift_card" && l.kind !== "membership" && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => stepQty(l.uid, -1)}
                          disabled={l.qty <= 1}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40 disabled:hover:bg-transparent"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm tabular-nums text-zinc-300">{l.qty}</span>
                        <button
                          type="button"
                          onClick={() => stepQty(l.uid, 1)}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}

                    <div className="w-28 text-right">
                      <div className={cn("text-sm tabular-nums", redeemed ? "text-violet-300" : "text-zinc-200")}>
                        {formatMoney(redeemed ? 0 : l.unitCents * l.qty)}
                      </div>
                      {l.qty > 1 && !redeemed && (
                        <div className="text-[11px] tabular-nums text-zinc-500">{formatMoney(l.unitCents)} each</div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeLine(l.uid)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
                      aria-label={`Remove ${l.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                   </div>
                      {/* The boosters this treatment is set up for, on the
                          treatment itself.

                            "Also at check out I was not able to add on to the
                             iv drip the add ons"

                          They were always on the Service picker, under
                          "Add-ons (with a drip)", but that means leaving the
                          drip, opening a list of 150 treatments and finding
                          them. Her menu already says which boosters go with
                          which drip, so they are offered here, on the line
                          she is looking at. */}
                      {l.kind === "service" && (() => {
                        const parent = services.find((x) => x.id === l.refId);
                        const offers = (parent?.addon_ids ?? [])
                          .map((id) => services.find((x) => x.id === id))
                          .filter((x): x is Service => !!x && !x.archived)
                          .filter((x) => !lines.some((other) => other.kind === "service" && other.refId === x.id));
                        if (offers.length === 0) return null;
                        return (
                          /* On a phone these stacked one per line and
                             squeezed the price and the quantity into a sliver
                             of the row, so there they are one swipeable line
                             instead. */
                          <div className="-mx-0.5 mt-1.5 flex items-center gap-1 overflow-x-auto px-0.5 pb-0.5 sm:flex-wrap sm:overflow-x-visible">
                            <span className="shrink-0 text-[11px] text-zinc-600">Add on:</span>
                            {offers.map((a) => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={() => addLine({
                                  kind: "service", refId: a.id, name: a.name,
                                  caption: a.category_name ?? undefined,
                                  unitCents: a.price_cents, qty: 1, taxable: a.taxable,
                                  unitOverride: false, redeemBalanceId: null,
                                })}
                                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-zinc-800/70 px-2 py-0.5 text-[11px] font-medium text-zinc-400 ring-1 ring-inset ring-zinc-700 hover:text-zinc-100"
                              >
                                <Plus className="h-3 w-3" />
                                {a.name.replace(/^Add /i, "")} {formatMoney(a.price_cents)}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Add-to-ticket row */}
          <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-800/70 pt-4">
            <Button variant="secondary" size="sm" leftIcon={<Sparkles className="h-3.5 w-3.5" />} onClick={() => setPicker("service")}>
              Service
            </Button>
            <Button variant="secondary" size="sm" leftIcon={<ShoppingBag className="h-3.5 w-3.5" />} onClick={() => setPicker("product")}>
              Retail
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Package className="h-3.5 w-3.5" />}
              onClick={() => setPicker("package")}
              disabled={!client}
              title={client ? undefined : "Select a client first — packages are sold to a client"}
            >
              Package
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Crown className="h-3.5 w-3.5" />}
              onClick={() => setPicker("membership")}
              disabled={!client}
              title={client ? undefined : "Select a client first — a membership is sold to a client"}
            >
              Membership
            </Button>
            <Button variant="secondary" size="sm" leftIcon={<Gift className="h-3.5 w-3.5" />} onClick={() => setPicker("gift_card")}>
              Gift card
            </Button>
            <Button variant="secondary" size="sm" leftIcon={<PlusCircle className="h-3.5 w-3.5" />} onClick={() => setPicker("custom")}>
              Custom item
            </Button>
          </div>
        </div>
      </div>

      {/* RIGHT — totals & payment */}
      <div className={cn(CARD_CLASS, "self-start lg:sticky lg:top-4")}>
        <h2 className="mb-3 text-sm font-semibold text-zinc-200">Totals</h2>

        <div className="space-y-2.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Subtotal</span>
            <span className="tabular-nums text-zinc-200">{formatMoney(totals.subtotal)}</span>
          </div>

          {/*
            "at the check out we have discounts but need buttons to select
             discount 10 15, 20 percent …. Also need reasons why we are
             discounting like included with purchase, Police/Fire, included
             with service - we do not charge for IV drip consultations …
             But for this new one I'm only able to put in a dollar amount with
             no reason"
          */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-400">Discount</span>
              <div className="w-24">
                <Input
                  value={discountStr}
                  onChange={(e) => { setDiscountStr(e.target.value); setDiscountPct(null); }}
                  placeholder="0.00"
                  inputMode="decimal"
                  className="h-8 text-right text-sm"
                  leftIcon={<span className="text-xs">$</span>}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {[10, 15, 20, 25, 50, 100].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => {
                    const next = discountPct === pct ? null : pct;
                    setDiscountPct(next);
                    setDiscountStr(next === null ? "" : (Math.round(totals.subtotal * next) / 100 / 100).toFixed(2));
                  }}
                  className={cn(
                    "rounded-md border px-2 py-0.5 text-[11px] font-medium",
                    discountPct === pct
                      ? "border-indigo-500 bg-indigo-500/15 text-indigo-200"
                      : "border-zinc-700 text-zinc-400 hover:text-zinc-200",
                  )}
                >
                  {pct}%
                </button>
              ))}
            </div>
            {(discountCents > 0 || discountPct !== null) && (
              <div className="space-y-1.5">
                {/* "I need to be able to write in reasons as well as have the
                    drop down options" - it was a dropdown only, so anything
                    that was not on her list could not be recorded at all. */}
                <Input
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  className="h-8 text-xs"
                  placeholder="Why? The client sees this on their receipt"
                  aria-label="Why this is discounted"
                />
                <div className="flex flex-wrap gap-1">
                  {DISCOUNT_REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setDiscountReason(r)}
                      className={cn(
                        "rounded-full border px-2 py-0.5 text-[11px]",
                        discountReason === r
                          ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
                          : "border-zinc-700 text-zinc-400 hover:text-zinc-200",
                      )}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-zinc-400">
              Tax <span className="text-xs text-zinc-600">({config.tax_rate_pct}%)</span>
            </span>
            <span className="tabular-nums text-zinc-200">{formatMoney(totals.tax)}</span>
          </div>

          {/* Tip */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Tip</span>
              <span className="tabular-nums text-zinc-200">{formatMoney(tipCents)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {[15, 20, 25].map((pct) => {
                const cents = Math.round((totals.serviceValue * pct) / 100);
                const active = tipCents === cents && cents > 0;
                return (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => { setTipCents(cents); setTipStr((cents / 100).toFixed(2)); }}
                    disabled={totals.serviceValue === 0}
                    className={cn(
                      "flex-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset disabled:opacity-40",
                      active
                        ? "bg-indigo-500/15 text-indigo-300 ring-indigo-500/40"
                        : "bg-zinc-800/60 text-zinc-400 ring-zinc-700 hover:text-zinc-200"
                    )}
                    title={totals.serviceValue === 0 ? "Add a service to suggest a tip" : formatMoney(cents)}
                  >
                    {pct}%
                  </button>
                );
              })}
              <div className="w-20">
                <Input
                  value={tipStr}
                  onChange={(e) => { setTipStr(e.target.value); setTipCents(parseDollars(e.target.value)); }}
                  placeholder="0.00"
                  inputMode="decimal"
                  className="h-7 text-right text-xs"
                  leftIcon={<span className="text-[10px]">$</span>}
                  aria-label="Custom tip amount"
                />
              </div>
            </div>
          </div>

          {/* Gift card */}
          {appliedGiftCard ? (
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-zinc-400">
                <Gift className="h-3.5 w-3.5 text-violet-400" />
                <span className="font-mono text-xs text-zinc-300">{appliedGiftCard.code}</span>
                <button
                  type="button"
                  onClick={() => setAppliedGiftCard(null)}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
                  aria-label="Remove gift card"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
              <span className="tabular-nums text-emerald-300">−{formatMoney(totals.giftApplied)}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <Input
                value={giftCodeStr}
                onChange={(e) => setGiftCodeStr(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void applyGiftCode(); }}
                placeholder="Gift card code"
                className="h-8 flex-1 font-mono text-xs"
                aria-label="Gift card code"
              />
              <Button variant="secondary" size="sm" onClick={() => void applyGiftCode()} loading={giftLookupBusy} disabled={!giftCodeStr.trim()}>
                Apply
              </Button>
            </div>
          )}
          {appliedGiftCard && (
            <p className="text-xs text-zinc-500">
              Balance {formatMoney(appliedGiftCard.balance_cents)} — {formatMoney(totals.giftApplied)} applied
            </p>
          )}

          {/* Account credit — membership credit, goodwill, refunds-to-credit. */}
          {creditCents > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-zinc-400">
                  <Wallet className="h-3.5 w-3.5 text-emerald-400" />
                  Account credit available
                </span>
                <span className="tabular-nums text-zinc-200">{formatMoney(creditCents)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400">
                  <input
                    type="checkbox"
                    checked={useCredit}
                    onChange={(e) => setUseCredit(e.target.checked)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  Use account credit
                </label>
                {useCredit && (
                  <span className="tabular-nums text-emerald-300">−{formatMoney(totals.creditApplied)}</span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-zinc-800 pt-2.5">
            <span className="font-semibold text-zinc-100">Total</span>
            <span className="text-base font-semibold tabular-nums text-zinc-100">{formatMoney(totals.total)}</span>
          </div>

          {/*
            "I need the clients to be charged the cc fees … I have already
             gave notice to my clients that credit card fees apply"

            Shown before the press rather than after it, because which button
            she reaches for is the thing that decides it. Cash takes the Total
            above; any card button takes the figure here.
          */}
          {Number(config.card_fee_pct ?? 0) > 0 && totals.total > 0 && (
            <div className="space-y-1.5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
              {totals.feeApplies ? (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-400">
                      {config.card_fee_label ?? "Card processing fee"}{" "}
                      <span className="text-zinc-600">({config.card_fee_pct}%)</span>
                    </span>
                    <span className="tabular-nums text-zinc-300">+{formatMoney(totals.cardFee)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-300">Paying by card</span>
                    <span className="text-sm font-semibold tabular-nums text-zinc-100">
                      {formatMoney(totals.total + totals.cardFee)}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-xs text-zinc-500">
                  {waiveCardFee
                    ? "No card fee on this sale - any card button takes the Total above."
                    : "This card is a debit, HSA or FSA card, so there is no card fee on it."}
                </p>
              )}
              <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400">
                <input
                  type="checkbox"
                  checked={waiveCardFee}
                  onChange={(e) => setWaiveCardFee(e.target.checked)}
                  className="h-4 w-4 accent-indigo-500"
                />
                No card fee on this sale
              </label>
              <p className="text-[11px] leading-relaxed text-zinc-600">
                Cash pays the Total above. Debit, HSA and FSA cards never carry the fee.
              </p>
            </div>
          )}
        </div>

        {lines.some((l) => l.kind === "membership") && (
          <p className="mt-4 rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-xs text-violet-200">
            <span className="font-medium">Text a payment link</span> to have the membership renew automatically on
            the client&apos;s card. Cash or the terminal pays this cycle only, and it comes due again at the end.
          </p>
        )}

        {/*
          "if the bill is 1000 and they want to pay 300 cash 200 on one card
           and 500 on another how do I do that"

          Off, every button takes the whole bill, exactly as before. On, the
          next press takes only what is typed here and the sale stays open
          with the rest owing - then the same buttons come back for it.
        */}
        {lines.length > 0 && totals.total > 0 && (
          <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900/30 p-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={splitCents !== null}
                onChange={(e) => setSplitCents(e.target.checked ? Math.round(totals.total / 2) : null)}
                className="h-4 w-4 rounded border-zinc-600 accent-indigo-500"
              />
              Split this bill across more than one payment
            </label>
            {splitCents !== null && (
              <div className="mt-2.5">
                <Label htmlFor="split-amt">Take now</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-500">$</span>
                  <Input
                    id="split-amt"
                    inputMode="decimal"
                    value={(splitCents / 100).toFixed(2)}
                    onChange={(e) => {
                      const n = Math.round(parseFloat(e.target.value.replace(/[^0-9.]/g, "")) * 100);
                      setSplitCents(Number.isFinite(n) ? Math.max(0, Math.min(n, totals.total)) : 0);
                    }}
                    className="w-32 tabular-nums"
                  />
                  <span className="text-xs text-zinc-500">
                    of {formatMoney(totals.total)} - {formatMoney(Math.max(0, totals.total - splitCents))} left after this
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment buttons */}
        <div className="mt-4 space-y-2">
          <Button
            className="w-full"
            leftIcon={<Banknote className="h-4 w-4" />}
            disabled={lines.length === 0 || submitting !== null}
            loading={submitting === "cash"}
            onClick={() => void submit("cash")}
          >
            Cash
          </Button>
          {/* "Matt who sets up the card equipment I have" - her Stripe Reader
              S700. One press here and the amount is ON the reader; she never
              types the total twice, and nothing is marked paid until the card
              actually goes through. Greyed when the reader is asleep rather
              than failing with a client standing there. */}
          {readers.length === 0 && readerPlace && (
            <p className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-center text-[11px] text-zinc-400">
              No card reader paired yet. On the reader: Settings, then Generate pairing code, and
              type those three words into <Link to="/settings" className="font-medium text-indigo-300 hover:text-indigo-200">Settings, Card reader</Link>.
            </p>
          )}
          {readers.length > 0 && (
            <div>
              <Button
                className="w-full"
                variant={liveReader ? "primary" : "secondary"}
                leftIcon={<CreditCard className="h-4 w-4" />}
                disabled={lines.length === 0 || submitting !== null || !liveReader}
                loading={submitting === "card_terminal"}
                onClick={() => void submit("card_terminal")}
              >
                Charge on the reader
              </Button>
              <p className="mt-1 text-center text-[11px] text-zinc-600">
                {liveReader
                  ? `${liveReader.label ?? "Card reader"} is ready - the amount goes to it`
                  : "The reader is asleep or off the wifi. Wake it and this comes back."}
              </p>
            </div>
          )}
          {/* Only where there is a machine to have pressed. "It says paid on
              the machine but it did not pay it" - this button charges nothing
              anywhere, it writes down that a machine of her own already did,
              and she has none. Turned on in Settings → Spa scheduling &
              checkout once there is one. */}
          {config?.own_card_machine && (
            <div>
              <Button
                variant="secondary"
                className="w-full"
                leftIcon={<CreditCard className="h-4 w-4" />}
                disabled={lines.length === 0 || submitting !== null}
                loading={submitting === "card_external"}
                onClick={() => void submit("card_external")}
              >
                Already paid, card machine
              </Button>
              <p className="mt-1 text-center text-[11px] text-zinc-600">writes it down, takes no money</p>
            </div>
          )}
          {/* "Have to take CC at booking. I think if the CC is on file we can
              do the charging at the desk." One press, no link to send, no
              card to ask for again. */}
          {cardOnFile ? (
            <div>
              <Button
                variant="secondary"
                className="w-full"
                leftIcon={<CreditCard className="h-4 w-4" />}
                disabled={lines.length === 0 || submitting !== null}
                loading={submitting === "card_on_file"}
                onClick={() => void submit("card_on_file")}
              >
                Charge the card on file
              </Button>
              {cards.length > 1 ? (
                <Select
                  value={cardId ?? ""}
                  onChange={(e) => setCardId(e.target.value)}
                  className="mt-1.5 h-8 text-xs"
                  aria-label="Which card to charge"
                >
                  {cards.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}{c.is_default ? " (default)" : ""}</option>
                  ))}
                </Select>
              ) : (
                <p className="mt-1 text-center text-[11px] text-zinc-600">{cardOnFile.label}</p>
              )}
            </div>
          ) : client ? (
            /* "I hit terminal to see if I could choose card on file" - she went
               looking for this and found nothing where it should have been, so
               the gap says what is missing instead of staying blank. */
            <div className="rounded-lg border border-dashed border-zinc-800 px-3 py-2 text-center">
              <p className="text-[11px] text-zinc-500">
                No card on file for {client.name.split(" ")[0]}.{" "}
                <Link to={`/contacts/${client.id}`} className="text-indigo-400 hover:text-indigo-300">
                  Add one on their file
                </Link>, or send a payment link below.
              </p>
            </div>
          ) : null}
          <Button
            variant="secondary"
            className="w-full"
            leftIcon={<Smartphone className="h-4 w-4" />}
            disabled={lines.length === 0 || submitting !== null}
            loading={submitting === "card_link" && !openPayHere}
            onClick={() => { setOpenPayHere(false); void submit("card_link"); }}
          >
            {practiceOn ? "Payment link (practice, nothing is sent)" : "Text a payment link"}
          </Button>
          {/* "I was not able to manually enter a card ... the link only allows
              to store the number and she did not want that plus she freaked
              out. I'll need the option to manually input the card"
              Same one-off payment page, opened on HER screen instead of
              texted, so the desk can key the card in with the client there.
              It takes the money once and keeps nothing - saving a card is the
              other button entirely. */}
          {/* "It does not let me manually enter a card let me show you blvd" -
              her old software put the card field right on the till with a tick
              for keeping it, so this does the same. The number goes from the
              field straight to the processor; we only ever see a token. */}
          {!practiceOn && cardKey && (
            <div>
              <Button
                variant="secondary"
                className="w-full"
                leftIcon={<CreditCard className="h-4 w-4" />}
                disabled={lines.length === 0 || submitting !== null}
                onClick={() => setTypeCard(true)}
              >
                Type a card in here
              </Button>
              <p className="mt-1 text-center text-[11px] text-zinc-600">
                charge it once, and keep it only if you tick
              </p>
            </div>
          )}
          {/* No key means the processor is not connected on this workspace;
              the old page-in-a-tab is better than nothing. */}
          {!practiceOn && !cardKey && (
            <div>
              <Button
                variant="secondary"
                className="w-full"
                leftIcon={<CreditCard className="h-4 w-4" />}
                disabled={lines.length === 0 || submitting !== null}
                loading={submitting === "card_link" && openPayHere}
                onClick={() => { setOpenPayHere(true); void submit("card_link"); }}
              >
                Type a card in here
              </Button>
              <p className="mt-1 text-center text-[11px] text-zinc-600">one-off charge, the card is not saved</p>
            </div>
          )}
          <Button
            variant={totals.total === 0 ? "secondary" : "ghost"}
            className="w-full"
            disabled={lines.length === 0 || submitting !== null}
            loading={submitting === "none"}
            onClick={() => void submit("none")}
          >
            {/* "is there a way to cash out without it saying cash? Maybe close
                out no money so the appointment is logged the discount is
                logged and they get a receipt" - when a voucher or a 100%
                discount has already covered the ticket there is no money to
                take, and calling that "unpaid" made her think the books were
                wrong. Nothing changes underneath: a zero ticket closes as
                settled either way. */}
            {totals.total === 0 ? "Close out - nothing owed" : "Save for later"}
          </Button>
        </div>

        {/* Options */}
        <div className="mt-4 space-y-2 border-t border-zinc-800/70 pt-3">
          <label className={cn("flex items-center gap-2 text-sm", client?.email ? "text-zinc-300" : "text-zinc-600")}>
            <input
              type="checkbox"
              checked={emailReceipt && !!client?.email}
              disabled={!client?.email}
              onChange={(e) => setEmailReceipt(e.target.checked)}
              className="h-4 w-4 accent-indigo-500"
            />
            Email receipt
          </label>
          {!client?.email && (
            <p className="pl-6 text-[11px] text-zinc-600">
              {client ? "No email on file for this client." : "Select a client with an email to send a receipt."}
            </p>
          )}
          {appointment && (
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={markCompleted}
                onChange={(e) => setMarkCompleted(e.target.checked)}
                className="h-4 w-4 accent-indigo-500"
              />
              Mark appointment completed
            </label>
          )}
        </div>
      </div>

      {discounting && (() => {
        const l = lines.find((x) => x.uid === discounting);
        return l ? (
          <LineDiscountModal
            line={l}
            onClose={() => setDiscounting(null)}
            onSave={(cents, reason) => {
              setLines((prev) => prev.map((x) => x.uid === discounting
                ? { ...x, discountCents: cents > 0 ? cents : undefined, discountReason: cents > 0 ? reason : undefined }
                : x));
              setDiscounting(null);
            }}
          />
        ) : null;
      })()}

      {/* Pickers */}
      <ServicePickerModal
        open={picker === "service"}
        onClose={() => setPicker(null)}
        services={services}
        onPick={(s) => {
          addLine({
            kind: "service", refId: s.id, name: s.name, caption: s.category_name ?? undefined,
            unitCents: s.price_cents, qty: 1, taxable: s.taxable, unitOverride: false, redeemBalanceId: null,
          });
          setPicker(null);
        }}
      />
      <ProductPickerModal
        open={picker === "product"}
        onClose={() => setPicker(null)}
        products={products}
        onPick={(p) => {
          addLine({
            kind: "product", refId: p.id, name: p.name, caption: "Retail",
            unitCents: p.amount_cents, qty: 1, taxable: true, unitOverride: false, redeemBalanceId: null,
          });
          setPicker(null);
        }}
      />
      <PackagePickerModal
        open={picker === "package"}
        onClose={() => setPicker(null)}
        packages={packages}
        clientName={client?.name ?? ""}
        onPick={(p) => {
          addLine({
            kind: "package", refId: p.id, name: p.name,
            caption: `${p.sessions} sessions${p.service_name ? ` · ${p.service_name}` : ""}`,
            unitCents: p.price_cents, qty: 1, taxable: false, unitOverride: false, redeemBalanceId: null,
            fulfilment: (p.default_fulfilment as Fulfilment | undefined) ?? "in_lounge", sessions: p.sessions,
          });
          setPicker(null);
        }}
      />
      <MembershipPickerModal
        open={picker === "membership"}
        onClose={() => setPicker(null)}
        memberships={memberships}
        clientName={client?.name ?? ""}
        onPick={(m) => {
          addLine({
            kind: "membership", refId: m.id, name: m.name,
            caption: `Renews ${everyCycle(m.interval)}`,
            unitCents: m.price_cents, qty: 1, taxable: false, unitOverride: false, redeemBalanceId: null,
          });
          setPicker(null);
        }}
      />
      <GiftCardLineModal
        open={picker === "gift_card"}
        onClose={() => setPicker(null)}
        onAdd={(amountCents, recipient, code) => {
          addLine({
            kind: "gift_card", refId: null, name: "Gift card",
            caption: [code && `Card ${code}`, recipient && `for ${recipient}`].filter(Boolean).join(" ") || undefined,
            unitCents: amountCents, qty: 1, taxable: false, unitOverride: false, redeemBalanceId: null,
            recipientName: recipient || undefined,
            giftCode: code || undefined,
          });
          setPicker(null);
        }}
      />
      <CustomLineModal
        open={picker === "custom"}
        onClose={() => setPicker(null)}
        onAdd={(name, amountCents, taxable) => {
          addLine({
            kind: "custom", refId: null, name, unitCents: amountCents, qty: 1,
            taxable, unitOverride: false, redeemBalanceId: null,
          });
          setPicker(null);
        }}
      />
      {addingPerson && (
        <AnotherClientModal
          onClose={() => setAddingPerson(false)}
          onPick={(c) => void addPersonToBill(c)}
        />
      )}
      {typeCard && cardKey && (
        <TypeCardModal
          cardKey={cardKey}
          amountCents={splitCents !== null && splitCents > 0 && splitCents < totals.total ? splitCents : totals.total}
          clientName={client?.name ?? null}
          canSave={!!client}
          onClose={() => setTypeCard(false)}
          onCharge={async (paymentMethodId, save) => {
            setTypeCard(false);
            await submit("card_manual", false, { paymentMethodId, save });
          }}
        />
      )}
    </div>
  );
}

/* ─── The card field at the desk ───────────────────────────────────────────── */
/**
 * "It does not let me manually enter a card let me show you blvd"
 *
 * Her old software put the card field on the till with a tick for keeping it,
 * so this does the same. The field belongs to the payment processor and is
 * only borrowed by this page: what she types goes straight to them and comes
 * back as a token for that one card, which is all the till ever handles.
 */
function TypeCardModal({
  cardKey, amountCents, clientName, canSave, onClose, onCharge,
}: {
  cardKey: string;
  amountCents: number;
  clientName: string | null;
  canSave: boolean;
  onClose: () => void;
  onCharge: (paymentMethodId: string, save: boolean) => void | Promise<void>;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const field = useRef<Awaited<ReturnType<typeof mountCardField>> | null>(null);
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  const [save, setSave] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    const el = box.current;
    if (!el) return;
    mountCardField(cardKey, el)
      .then((f) => {
        if (dead) { f.element.destroy(); return; }
        field.current = f;
        f.element.on("change", (e) => {
          setError(e.error?.message ?? null);
          setComplete(e.complete === true);
        });
        setReady(true);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "The card field could not be loaded."));
    return () => { dead = true; field.current?.element.destroy(); field.current = null; };
  }, [cardKey]);

  const charge = async () => {
    const f = field.current;
    if (!f || busy) return;
    setBusy(true);
    setError(null);
    const token = await tokeniseCard(f.stripe, f.element, clientName);
    if (!token.ok) { setError(token.error); setBusy(false); return; }
    await onCharge(token.id, save && canSave);
  };

  return (
    <Modal open onClose={onClose} title={`Take ${formatMoney(amountCents)} on a card`} size="sm">
      <div className="space-y-3">
        <div>
          <Label>Card</Label>
          {/* The processor's own field lives in here. */}
          {/* The field inside reads this box's own colour, so it follows the
              palette rather than guessing: `text-zinc-100` is near-black in
              the practice's light theme and near-white if it is ever flipped
              back, which is exactly what the card digits should do. */}
          <div
            ref={box}
            className="rounded-lg border border-zinc-700 bg-zinc-900/80 px-3 py-3 text-zinc-100"
          />
          {!ready && !error && <p className="mt-1 text-[11px] text-zinc-500">Getting the card field ready…</p>}
        </div>
        <label className={cn("flex items-center gap-2 text-sm", canSave ? "cursor-pointer text-zinc-300" : "text-zinc-600")}>
          <input
            type="checkbox"
            checked={save && canSave}
            disabled={!canSave}
            onChange={(e) => setSave(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-600 accent-indigo-500"
          />
          Keep this card on their file
        </label>
        <p className="text-[11px] text-zinc-500">
          {canSave
            ? save
              ? "Charged now, and kept for next time so you can charge it from the desk."
              : "Charged once. Nothing is kept."
            : "Pick a client first if you want to keep the card."}
        </p>
        {error && (
          <p className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</p>
        )}
        <p className="text-[11px] text-zinc-600">
          The number goes straight to the payment processor. It never reaches us and is never stored here.
        </p>
      </div>
      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button size="sm" loading={busy} disabled={!ready || !complete} onClick={() => void charge()}>
          Charge {formatMoney(amountCents)}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/* ─── Client picker card ───────────────────────────────────────────────────── */

function ClientCard({
  client,
  balances,
  redemptions,
  onSelect,
  onClear,
  alsoFor = [],
  onAddPerson,
  onDropPerson,
}: {
  client: ClientLite | null;
  balances: PackageBalance[];
  redemptions: PackageRedemption[];
  onSelect: (c: ClientLite) => void;
  onClear: () => void;
  /** "one bill but two separate orders" — the others this bill covers. */
  alsoFor?: { contact_id: string; name: string }[];
  onAddPerson?: () => void;
  onDropPerson?: (contactId: string) => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (!query) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get<{ contacts: Contact[] }>("/contacts", { q: query, limit: 8 })
        .then((d) => setResults(d.contacts))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className={CARD_CLASS}>
      <h2 className="mb-3 text-sm font-semibold text-zinc-200">Client</h2>
      {client && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-zinc-800/70 py-1 pl-2 pr-1 text-sm text-zinc-200 ring-1 ring-inset ring-zinc-700">
            <User className="h-3.5 w-3.5 text-zinc-400" />
            <Link to={`/contacts/${client.id}`} className="hover:text-indigo-300 hover:underline">
              {client.name}
            </Link>
            <button
              type="button"
              onClick={onClear}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-700 hover:text-zinc-200"
              aria-label="Clear client"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
          <span className="text-xs text-zinc-500">
            {[client.email, formatPhone(client.phone)].filter(Boolean).join(" · ") || "No contact details on file"}
          </span>
        </div>
      )}

      {(alsoFor.length > 0 || onAddPerson) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-800/70 pt-3">
          {alsoFor.length > 0 && <span className="text-xs text-zinc-500">Also on this bill:</span>}
          {alsoFor.map((p) => (
            <span key={p.contact_id}
              className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800/70 py-1 pl-2 pr-1 text-xs text-zinc-200 ring-1 ring-inset ring-zinc-700">
              {p.name}
              <button type="button" onClick={() => onDropPerson?.(p.contact_id)}
                aria-label={`Take ${p.name} off this bill`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-700 hover:text-zinc-200">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          {onAddPerson && (
            <button type="button" onClick={onAddPerson}
              className="text-xs font-medium text-indigo-300 hover:text-indigo-200">
              + Someone else on this bill
            </button>
          )}
        </div>
      )}

      {client && (balances.length > 0 || redemptions.length > 0) && (
        <div className="mt-3 border-t border-zinc-800/70 pt-3">
          {balances.length > 0 && (
            <ul className="space-y-1">
              {balances.map((b) => (
                <li key={b.id} className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="flex min-w-0 items-baseline gap-1.5 text-zinc-400">
                    <Package className="h-3 w-3 shrink-0 self-center text-zinc-500" />
                    <span className="truncate">
                      {b.package_name ?? "Package"}
                      {b.service_name ? <span className="text-zinc-600"> · {b.service_name}</span> : null}
                    </span>
                  </span>
                  <span className="shrink-0 tabular-nums text-zinc-300">
                    {b.sessions_remaining} of {b.sessions_total} left
                  </span>
                </li>
              ))}
            </ul>
          )}

          {redemptions.length > 0 && (
            <div className={cn(balances.length > 0 && "mt-2")}>
              <button
                type="button"
                onClick={() => setUsageOpen((o) => !o)}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
                aria-expanded={usageOpen}
              >
                <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", usageOpen && "rotate-90")} />
                Package usage ({redemptions.length})
              </button>
              {usageOpen && (
                <ul className="mt-1.5 space-y-1">
                  {redemptions.map((r) => (
                    <li key={r.id} className="flex items-baseline justify-between gap-3 text-xs">
                      <span className="min-w-0 truncate text-zinc-400">
                        <span className="text-zinc-500">{formatDate(r.created_at)}</span>
                        {" — "}{r.package_name ?? "Package"}
                        {r.service_name ? ` — ${r.service_name}` : ""}
                        {r.sessions > 1 ? ` (${r.sessions} sessions)` : ""}
                      </span>
                      <span className="shrink-0 font-mono text-[11px] text-zinc-600">{r.invoice_number ?? "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {!client && (
        <div className="relative">
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Search clients by name, phone, or email…"
            leftIcon={searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          />
          {open && q.trim() !== "" && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40">
              {results.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-zinc-500">
                  {searching ? "Searching…" : "No clients match that search."}
                </p>
              ) : (
                results.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onSelect({ id: c.id, name: fullName(c), email: c.email, phone: c.phone });
                      setQ("");
                      setResults([]);
                      setOpen(false);
                    }}
                    className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left hover:bg-zinc-800/60"
                  >
                    <span className="truncate text-sm text-zinc-200">{fullName(c)}</span>
                    <span className="shrink-0 text-xs text-zinc-500">
                      {formatPhone(c.phone) || c.email || ""}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
          <p className="mt-2 text-xs text-zinc-600">Walk-in (no client) — leave empty to ring up without a client.</p>
        </div>
      )}
    </div>
  );
}

/* ─── Picker modals ────────────────────────────────────────────────────────── */

/**
 * Taking money off ONE line, with the reason the client will read.
 *
 * Amount or percent, because she thinks in both ("percentage off or free or
 * partial discount"), and the same reasons the whole-ticket discount offers
 * so a report can group them.
 */
function LineDiscountModal({
  line, onClose, onSave,
}: {
  line: Line;
  onClose: () => void;
  onSave: (cents: number, reason: string) => void;
}) {
  const gross = line.unitCents * Math.max(1, line.qty);
  const [amount, setAmount] = useState(line.discountCents ? (line.discountCents / 100).toFixed(2) : "");
  const [pct, setPct] = useState("");
  const [reason, setReason] = useState(line.discountReason ?? "");

  const cents = (() => {
    const p = Number(pct);
    if (pct.trim() && Number.isFinite(p) && p > 0) return Math.min(gross, Math.round(gross * (p / 100)));
    const a = Number(amount);
    return Number.isFinite(a) && a > 0 ? Math.min(gross, Math.round(a * 100)) : 0;
  })();

  return (
    <Modal open onClose={onClose} title={`Discount: ${line.name}`} size="sm">
      <div className="space-y-3">
        <p className="text-xs text-zinc-500">
          Full price {formatMoney(gross)}. They pay {formatMoney(Math.max(0, gross - cents))}.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="disc-amt">Amount off ($)</Label>
            <Input id="disc-amt" inputMode="decimal" value={amount} placeholder="0.00"
              onChange={(e) => { setAmount(e.target.value); setPct(""); }} />
          </div>
          <div>
            <Label htmlFor="disc-pct">or percent</Label>
            <Input id="disc-pct" inputMode="decimal" value={pct} placeholder="e.g. 20"
              onChange={(e) => { setPct(e.target.value); setAmount(""); }} />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[10, 20, 50].map((p) => (
            <button key={p} type="button" onClick={() => { setPct(String(p)); setAmount(""); }}
              className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 hover:border-zinc-500">
              {p}% off
            </button>
          ))}
          <button type="button" onClick={() => { setPct("100"); setAmount(""); }}
            className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 hover:border-zinc-500">
            Free
          </button>
        </div>
        <div>
          <Label htmlFor="disc-why">Why?</Label>
          <Input id="disc-why" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="The client sees this on their receipt" />
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {DISCOUNT_REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setReason(r)}
                className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 hover:border-zinc-500">
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ModalFooter>
        {(line.discountCents ?? 0) > 0 && (
          <Button variant="ghost" className="mr-auto text-zinc-400" onClick={() => onSave(0, "")}>
            Remove discount
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button disabled={cents <= 0 || !reason.trim()} onClick={() => onSave(cents, reason.trim())}>
          Take {formatMoney(cents)} off
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ServicePickerModal({
  open,
  onClose,
  services,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  services: Service[];
  onPick: (s: Service) => void;
}) {
  // "We have to be able to search for anything added on" — 170 treatments
  // and the boosters, filtered as she types (name, category, or dose).
  const [query, setQuery] = useState("");
  useEffect(() => { if (open) setQuery(""); }, [open]);
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = services.filter((s) => !s.archived)
      .filter((s) => !q || s.name.toLowerCase().includes(q) || (s.category_name ?? "").toLowerCase().includes(q));
    const map = new Map<string, Service[]>();
    for (const s of active) {
      const key = s.category_name ?? "Other";
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    return [...map.entries()];
  }, [services, query]);

  return (
    <Modal open={open} onClose={onClose} title="Add a service" size="md">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search treatments and add-ons…"
        aria-label="Search services"
        leftIcon={<Search className="h-4 w-4" />}
        autoFocus
        className="mb-3"
      />
      {groups.length === 0 ? (
        <p className="py-4 text-center text-sm text-zinc-500">
          {query.trim() ? `Nothing matches "${query}".` : "No services set up yet."}
        </p>
      ) : (
        <div className="max-h-96 space-y-4 overflow-y-auto pr-1">
          {groups.map(([category, items]) => (
            <div key={category}>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">{category}</h3>
              <div className="space-y-0.5">
                {items.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onPick(s)}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-zinc-800/60"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-zinc-200">{s.name}</span>
                      <span className="text-xs text-zinc-500">{s.duration_minutes} min</span>
                    </span>
                    <span className="shrink-0 text-sm tabular-nums text-zinc-300">{formatMoney(s.price_cents)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/**
 * Everything she sells is searchable at the till.
 *
 * "At checkout can we be able to search pkgs memberships ect"
 *
 * Only the service picker had a search box. She has 26 packages and a screen
 * of products, so the rest meant scrolling with a client waiting.
 */
function ProductPickerModal({
  open,
  onClose,
  products,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  products: Product[];
  onPick: (p: Product) => void;
}) {
  const [query, setQuery] = useState("");
  const active = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter((p) => !p.archived)
      .filter((p) => !q || `${p.name} ${p.description ?? ""}`.toLowerCase().includes(q));
  }, [products, query]);
  return (
    <Modal open={open} onClose={onClose} title="Add a retail product" size="md">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search products…"
        aria-label="Search products"
        leftIcon={<Search className="h-4 w-4" />}
        autoFocus
        className="mb-3"
      />
      {active.length === 0 ? (
        <p className="py-4 text-center text-sm text-zinc-500">
          {query.trim() ? `Nothing matches "${query}".` : "No retail products yet. Add them under Manage, Menu & seats, Products."}
        </p>
      ) : (
        <div className="max-h-96 space-y-0.5 overflow-y-auto pr-1">
          {active.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-zinc-800/60"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-zinc-200">{p.name}</span>
                {p.description && <span className="block truncate text-xs text-zinc-500">{p.description}</span>}
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm tabular-nums text-zinc-300">{formatMoney(p.amount_cents)}</span>
                {p.track_inventory && (
                  <span className={cn("block text-[11px] tabular-nums", (p.in_stock ?? 0) <= 0 ? "text-amber-300" : "text-zinc-500")}>
                    {(p.in_stock ?? 0) <= 0 ? "Count says none left" : `${p.in_stock} in stock`}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function PackagePickerModal({
  open,
  onClose,
  packages,
  clientName,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  packages: SpaPackage[];
  clientName: string;
  onPick: (p: SpaPackage) => void;
}) {
  const [query, setQuery] = useState("");
  const active = useMemo(() => {
    const q = query.trim().toLowerCase();
    return packages
      .filter((p) => !p.archived)
      .filter((p) => !q || `${p.name} ${p.service_name ?? ""}`.toLowerCase().includes(q));
  }, [packages, query]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sell a package"
      description={clientName ? `Sessions will be credited to ${clientName}.` : undefined}
      size="md"
    >
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search packages…"
        aria-label="Search packages"
        leftIcon={<Search className="h-4 w-4" />}
        autoFocus
        className="mb-3"
      />
      {active.length === 0 ? (
        <p className="py-4 text-center text-sm text-zinc-500">
          {query.trim() ? `Nothing matches "${query}".` : "No packages set up yet."}
        </p>
      ) : (
        <div className="max-h-96 space-y-0.5 overflow-y-auto pr-1">
          {active.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-zinc-800/60"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-zinc-200">{p.name}</span>
                <span className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
                  {p.sessions} sessions{p.service_name ? ` · ${p.service_name}` : ""}
                  {p.take_home && <Badge color="teal" size="xs">Can take home</Badge>}
                </span>
              </span>
              <span className="shrink-0 text-sm tabular-nums text-zinc-300">{formatMoney(p.price_cents)}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function MembershipPickerModal({
  open,
  onClose,
  memberships,
  clientName,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  memberships: Membership[];
  clientName: string;
  onPick: (m: Membership) => void;
}) {
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return memberships.filter((m) => !q || m.name.toLowerCase().includes(q));
  }, [memberships, query]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sell a membership"
      description={clientName ? `${clientName} gets this cycle's perks as soon as it's paid. Paid by payment link, it renews automatically on their card.` : undefined}
      size="md"
    >
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search memberships…"
        aria-label="Search memberships"
        leftIcon={<Search className="h-4 w-4" />}
        autoFocus
        className="mb-3"
      />
      {shown.length === 0 ? (
        <p className="py-4 text-center text-sm text-zinc-500">
          {query.trim()
            ? `Nothing matches "${query}".`
            : "No memberships set up yet — build one under Services & Rooms → Memberships."}
        </p>
      ) : (
        <div className="max-h-96 space-y-0.5 overflow-y-auto pr-1">
          {shown.map((m) => {
            const perks = [
              m.credit_cents > 0 ? `${formatMoney(m.credit_cents)} credit` : null,
              m.discount_pct > 0 ? `${m.discount_pct}% off` : null,
            ].filter(Boolean).join(" · ");
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onPick(m)}
                className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-zinc-800/60"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-zinc-200">{m.name}</span>
                  <span className="text-xs text-zinc-500">{perks || m.description || "Membership"}</span>
                </span>
                <span className="shrink-0 text-sm tabular-nums text-zinc-300">
                  {formatMoney(m.price_cents)}
                  <span className="text-xs text-zinc-500">{perCycle(m.interval)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

function GiftCardLineModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (amountCents: number, recipient: string, code: string) => void;
}) {
  const [amountStr, setAmountStr] = useState("");
  const [recipient, setRecipient] = useState("");
  const [code, setCode] = useState("");
  const amount = parseDollars(amountStr);

  const close = () => { setAmountStr(""); setRecipient(""); setCode(""); onClose(); };
  const add = () => {
    if (amount <= 0) return;
    onAdd(amount, recipient.trim(), code.trim());
    setAmountStr("");
    setRecipient("");
    setCode("");
  };

  return (
    <Modal open={open} onClose={close} title="Sell a gift card" size="sm">
      <div className="space-y-3">
        <div>
          <Label htmlFor="gc-amount">Amount</Label>
          <Input
            id="gc-amount"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="100.00"
            inputMode="decimal"
            autoFocus
            leftIcon={<span className="text-xs">$</span>}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
        </div>
        <div>
          {/* "I have a hole stack I was able to put the amount onto the card
              with blvd" — her cards are printed with their own numbers, so the
              desk types the one off the card in front of them. Left blank, the
              till makes one up, which is what you want for an emailed card. */}
          <Label htmlFor="gc-code">Card number <span className="text-zinc-600">(from the card)</span></Label>
          <Input
            id="gc-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Leave blank to generate one"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
        </div>
        <div>
          <Label htmlFor="gc-recipient">Recipient name <span className="text-zinc-600">(optional)</span></Label>
          <Input
            id="gc-recipient"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="Who is it for?"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={close}>Cancel</Button>
        <Button onClick={add} disabled={amount <= 0}>
          Add {amount > 0 ? formatMoney(amount) : "gift card"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function CustomLineModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (name: string, amountCents: number, taxable: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [amountStr, setAmountStr] = useState("");
  const [taxable, setTaxable] = useState(true);
  const amount = parseDollars(amountStr);
  const valid = name.trim() !== "" && amount > 0;

  const close = () => { setName(""); setAmountStr(""); setTaxable(true); onClose(); };
  const add = () => {
    if (!valid) return;
    onAdd(name.trim(), amount, taxable);
    setName("");
    setAmountStr("");
    setTaxable(true);
  };

  return (
    <Modal open={open} onClose={close} title="Add a custom item" size="sm">
      <div className="space-y-3">
        <div>
          <Label htmlFor="custom-name">Name</Label>
          <Input
            id="custom-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Consultation fee"
            autoFocus
          />
        </div>
        <div>
          <Label htmlFor="custom-amount">Price</Label>
          <Input
            id="custom-amount"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
            leftIcon={<span className="text-xs">$</span>}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={taxable}
            onChange={(e) => setTaxable(e.target.checked)}
            className="h-4 w-4 accent-indigo-500"
          />
          Taxable
        </label>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={close}>Cancel</Button>
        <Button onClick={add} disabled={!valid}>Add item</Button>
      </ModalFooter>
    </Modal>
  );
}

/* ─── Result panels ────────────────────────────────────────────────────────── */

function ResultPanel({
  result,
  onPaid,
  onNewSale,
}: {
  result: CheckoutResult;
  onPaid: (r: CheckoutResult) => void;
  onNewSale: () => void;
}) {
  const toast = useToast();
  const [checking, setChecking] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  const checkPayment = useCallback(async (silent: boolean): Promise<boolean> => {
    try {
      const d = await api.post<{ ok: boolean; status: "paid" | "open" }>(
        `/pos/invoices/${result.invoice_id}/check-payment`
      );
      if (d.status === "paid") {
        onPaid({ ...result, status: "paid" });
        toast.success("Payment received", `${result.number} · ${formatMoney(result.total_cents)}`);
        return true;
      }
      if (!silent) toast.info("Not paid yet", "The payment link hasn't been completed.");
      return false;
    } catch (e) {
      if (!silent) toast.error("Couldn't check payment", errMessage(e));
      return false;
    }
  }, [result, onPaid, toast]);

  /* Auto-poll the payment link every 4s for ~5 minutes. */
  useEffect(() => {
    if (result.status !== "open" || result.method !== "card_link" || !result.pay_url) return;
    let polls = 0;
    const interval = setInterval(() => {
      polls += 1;
      if (polls > 75) { // ~5 min at 4s
        clearInterval(interval);
        setPollTimedOut(true);
        return;
      }
      void checkPayment(true);
    }, 4000);
    return () => clearInterval(interval);
  }, [result.status, result.method, result.pay_url, checkPayment]);

  const copyLink = () => {
    if (!result.pay_url) return;
    navigator.clipboard.writeText(result.pay_url)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Couldn't copy — copy it manually"));
  };

  /* Practice payment link: nothing went to the client; play them paying. */
  if (result.status === "open" && result.method === "card_link" && result.practice) {
    return (
      <div className={cn(CARD_CLASS, "mx-auto max-w-lg text-center")}>
        <h2 className="text-base font-semibold text-zinc-100">
          Practice payment link, {result.number} · {formatMoney(result.total_cents)}
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          In real use the client gets a text with a secure card page. Nothing was sent. Press the button to act out the client paying.
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button
            loading={checking}
            onClick={async () => {
              setChecking(true);
              try {
                await api.post(`/pos/invoices/${result.invoice_id}/practice-pay`);
                onPaid({ ...result, status: "paid" });
              } catch (e) {
                toast.error("Couldn't record it", errMessage(e));
              } finally { setChecking(false); }
            }}
          >
            The client paid
          </Button>
          <Button variant="ghost" onClick={onNewSale}>New sale</Button>
        </div>
      </div>
    );
  }

  /* Paid — for cash / terminal immediately, or once the link poll flips. */
  if (result.status === "paid") {
    return (
      <div className={cn(CARD_CLASS, "mx-auto max-w-lg text-center")}>
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
          <CheckCircle2 className="h-6 w-6 text-emerald-400" />
        </div>
        <h2 className="text-lg font-semibold text-zinc-100">
          {result.practice ? "Practice sale" : "Paid"}, {result.number} · {formatMoney(result.total_cents)}
        </h2>
        {result.practice && (
          <p className="mt-1 text-sm text-amber-300">Nothing was charged and nothing was sent to the client.</p>
        )}
        {result.gift_card_applied_cents > 0 && (
          <p className="mt-1 text-sm text-zinc-400">
            Gift card covered {formatMoney(result.gift_card_applied_cents)}.
          </p>
        )}
        {result.credit_applied_cents > 0 && (
          <p className="mt-1 text-sm text-zinc-400">
            {formatMoney(result.credit_applied_cents)} paid from account credit.
          </p>
        )}
        {/* Which card, so a query a week later has an answer on the receipt. */}
        {result.charged_card && (
          <p className="mt-1 text-sm text-zinc-400">
            {result.method === "card_terminal" ? "Taken on the reader" : "Charged"} - {result.charged_card}.
          </p>
        )}
        {!result.practice && (
          <p className="mt-1 text-sm text-zinc-500">
            {result.receiptEmail
              ? `A receipt is on its way to ${result.receiptEmail}.`
              : "No receipt was emailed for this sale."}
          </p>
        )}
        <div className="mt-5">
          <Button onClick={onNewSale} leftIcon={<Plus className="h-4 w-4" />}>New sale</Button>
        </div>
      </div>
    );
  }

  /* Open with a pay link — waiting on the client. */
  if (result.method === "card_link" && result.pay_url) {
    return (
      <div className={cn(CARD_CLASS, "mx-auto max-w-lg")}>
        <div className="mb-3 flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-indigo-400" />
          <h2 className="text-base font-semibold text-zinc-100">
            Awaiting payment — {result.number} · {formatMoney(result.total_cents)}
          </h2>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-zinc-300">{result.pay_url}</span>
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            aria-label="Copy payment link"
          >
            <Copy className="h-4 w-4" />
          </button>
          <a
            href={result.pay_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            aria-label="Open payment link"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Text or show this link to the client - or open it here and type their card in yourself.
          Either way it takes the money once and does not keep the card.
        </p>
        <div className="mt-2">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<CreditCard className="h-3.5 w-3.5" />}
            onClick={() => window.open(result.pay_url!, "_blank", "noopener")}
          >
            Open it here and type the card
          </Button>
        </div>
        {result.auto_renews && (
          <p className="mt-1 text-xs text-violet-300">
            Paying this link also turns on automatic billing for the membership.
          </p>
        )}
        <p className="mt-1 flex items-center gap-1.5 text-xs text-zinc-600">
          {pollTimedOut ? (
            <>Stopped watching here, but it will still mark itself paid when the client pays.</>
          ) : (
            <><Loader2 className="h-3 w-3 animate-spin" /> Watching for the payment…</>
          )}
        </p>
        <div className="mt-4 flex items-center gap-2">
          <Button
            variant="secondary"
            loading={checking}
            onClick={() => { setChecking(true); void checkPayment(false).finally(() => setChecking(false)); }}
          >
            Check payment
          </Button>
          <Button variant="ghost" onClick={onNewSale}>New sale</Button>
        </div>
      </div>
    );
  }

  /* Saved unpaid. */
  return (
    <div className={cn(CARD_CLASS, "mx-auto max-w-lg text-center")}>
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-zinc-800/70">
        <FileText className="h-6 w-6 text-zinc-300" />
      </div>
      <h2 className="text-base font-semibold text-zinc-100">
        Saved as unpaid invoice {result.number} — find it under Payments → Invoices.
      </h2>
      <p className="mt-1 text-sm text-zinc-500">Total {formatMoney(result.total_cents)}</p>
      <div className="mt-5">
        <Button onClick={onNewSale} leftIcon={<Plus className="h-4 w-4" />}>New sale</Button>
      </div>
    </div>
  );
}

/* ─── Gift cards tab ───────────────────────────────────────────────────────── */

function GiftCardsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [cards, setCards] = useState<GiftCard[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ gift_cards: GiftCard[] }>("/pos/gift-cards")
      .then((d) => { if (!cancelled) setCards(d.gift_cards); })
      .catch((e) => { if (!cancelled) setLoadError(errMessage(e)); });
    return () => { cancelled = true; };
  }, []);

  const toggle = async (gc: GiftCard) => {
    if (gc.active) {
      const ok = await confirm({
        title: "Disable this gift card?",
        description: `${gc.code} still has ${formatMoney(gc.balance_cents)} on it. Disabled cards can't be applied at checkout until re-enabled.`,
        confirmLabel: "Disable",
        danger: true,
      });
      if (!ok) return;
    }
    setTogglingId(gc.id);
    try {
      const d = await api.patch<{ gift_card: GiftCard }>(`/pos/gift-cards/${gc.id}`, { active: !gc.active });
      setCards((prev) => prev?.map((c) => (c.id === gc.id ? d.gift_card : c)) ?? prev);
      toast.success(d.gift_card.active ? "Gift card enabled" : "Gift card disabled", gc.code);
    } catch (e) {
      toast.error("Couldn't update gift card", errMessage(e));
    } finally {
      setTogglingId(null);
    }
  };

  if (loadError) {
    return <EmptyState icon={Gift} title="Couldn't load gift cards" description={loadError} />;
  }
  if (cards === null) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 rounded-lg" />
        <Skeleton className="h-10 rounded-lg" />
        <Skeleton className="h-10 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-500">
        Sell a gift card from the Sale tab — add &ldquo;Gift card&rdquo; to a ticket.
      </p>
      {cards.length === 0 ? (
        <EmptyState
          icon={Gift}
          title="No gift cards yet"
          description="Gift cards you sell will show up here with their remaining balance."
        />
      ) : (
        <div className={cn(CARD_CLASS, "overflow-x-auto p-0")}>
          <table className="w-full min-w-[40rem] text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-xs text-zinc-500">
                <th className="px-4 py-2.5 font-medium">Code</th>
                <th className="px-4 py-2.5 font-medium">Balance</th>
                <th className="px-4 py-2.5 font-medium">Purchaser</th>
                <th className="px-4 py-2.5 font-medium">Recipient</th>
                <th className="px-4 py-2.5 font-medium">Created</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {cards.map((gc) => (
                <tr key={gc.id} className="hover:bg-zinc-800/20">
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-200">{gc.code}</td>
                  <td className="px-4 py-2.5 tabular-nums text-zinc-200">
                    {formatMoney(gc.balance_cents)}
                    <span className="text-zinc-500"> / {formatMoney(gc.initial_cents)}</span>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-300">{gc.purchaser_name ?? "—"}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{gc.recipient_name ?? "—"}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{formatDate(gc.created_at)}</td>
                  <td className="px-4 py-2.5">
                    {gc.active
                      ? <Badge color="emerald" size="xs">Active</Badge>
                      : <Badge color="zinc" size="xs">Disabled</Badge>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      variant={gc.active ? "ghost" : "secondary"}
                      size="sm"
                      loading={togglingId === gc.id}
                      onClick={() => void toggle(gc)}
                    >
                      {gc.active ? "Disable" : "Enable"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Who else is on this bill.
 *
 *   "how do I combine clients at checkout - for one bill but two separate
 *    orders"
 *
 * Picks the person; the ticket then pulls their booked treatments in,
 * tagged with their name, and paying closes their visit too.
 */
function AnotherClientModal({
  onClose, onPick,
}: { onClose: () => void; onPick: (c: Contact) => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (!query) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get<{ contacts: Contact[] }>("/contacts", { q: query, limit: 8 })
        .then((d) => setResults(d.contacts))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <Modal open onClose={onClose} title="Someone else on this bill" size="sm">
      <div className="space-y-3">
        <p className="text-sm text-zinc-400">
          Their treatments join this ticket and their visit closes when it is paid. The bill
          itself, and the receipt, stay in the payer&rsquo;s name. Their own vouchers are not used.
        </p>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email or mobile" />
        {searching && <p className="text-xs text-zinc-500">Looking…</p>}
        <ul className="max-h-64 divide-y divide-zinc-800/60 overflow-y-auto rounded-lg border border-zinc-800">
          {!searching && q.trim() && results.length === 0 && (
            <li className="px-3 py-3 text-sm text-zinc-500">Nobody by that name.</li>
          )}
          {results.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => onPick(c)}
                className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-zinc-800/60">
                <span className="text-sm text-zinc-100">
                  {[c.first_name, c.last_name].filter(Boolean).join(" ") || "Client"}
                </span>
                <span className="text-[11px] text-zinc-500">
                  {[c.email, formatPhone(c.phone)].filter(Boolean).join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
